import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ActivityLogSection, SavedRepliesManager, WeeklySummaryCard } from '../components/settings/SettingsExtras.jsx';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usersApi } from '../api/users.api.js';
import { settingsApi } from '../api/settings.api.js';
import { paymentsApi } from '../api/payments.api.js';
import { automationsApi } from '../api/automations.api.js';
import { aiAgentApi } from '../api/aiAgent.api.js';
import { linksApi } from '../api/links.api.js';
import { whatsappWebApi } from '../api/whatsappWeb.api.js';
import { Modal } from '../components/shared/Modal.jsx';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { useAuthStore } from '../store/authStore.js';
import { useSwipeNavigation } from '../hooks/useSwipeNavigation.js';
import { loadPaddleSdk, paddleCheckoutItems } from '../utils/paddleCheckout.js';
import { IconLink, IconPhone, IconGlobe, IconContacts, IconKey, IconCopy, IconSun, IconInfo, IconTrash, IconRefresh, IconNote, IconDollar, IconClock } from '../components/icons/Icons.jsx';
import {
  hasApiAccess,
  hasAutomation,
  hasCalls,
  hasDirectWhatsApp,
  hasEmail,
  hasInbox,
  hasShortLinks,
  hasSms,
} from '../utils/tenantFeatures.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function copyText(text) {
  navigator.clipboard.writeText(text)
    .then(() => toast.success('Copied!'))
    .catch(() => toast.error('Copy failed'));
}

function money(cents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(cents || 0) / 100);
}

function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="py-3" style={{ borderBottom: '1px solid var(--border)' }}>
      <p className="text-[11px] font-medium mb-1.5" style={{ color: 'var(--textDim)' }}>{label}</p>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <code
          className="flex-1 min-w-0 rounded-[8px] px-3 py-2 text-xs font-mono whitespace-pre-wrap break-anywhere"
          style={{ background: 'var(--bg)', color: 'var(--accent)', border: '1px solid var(--border)' }}
        >
          {value}
        </code>
        <button
          onClick={handleCopy}
          className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-medium flex-shrink-0 transition-all"
          style={{
            background: copied ? 'rgba(34,201,122,.15)' : 'var(--card)',
            color: copied ? 'var(--success)' : 'var(--textMuted)',
            border: '1px solid var(--border)',
          }}
        >
          <IconCopy size={12} />
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

const ROLE_STYLE = {
  ADMIN:   { bg: 'rgba(245,166,35,.1)',  text: 'var(--warning)' },
  MANAGER: { bg: 'rgba(59,167,255,.1)',  text: 'var(--info)'    },
  AGENT:   { bg: 'var(--card)',          text: 'var(--textMuted)' },
};

const workspaceSchema = z.object({
  name: z.string().min(1, 'Workspace name is required'),
  brandName: z.string().min(1, 'Brand name is required'),
  ownerEmail: z.string().email('Valid owner email required').optional().or(z.literal('')),
  supportEmail: z.string().email('Valid support email required').optional().or(z.literal('')),
  logoUrl: z.string().url('Enter a valid logo URL').optional().or(z.literal('')),
  primaryColor: z.string().regex(/^#[0-9a-f]{6}$/i, 'Use a hex color'),
  platformSubdomain: z.string().optional(),
  primaryDomain: z.string().optional(),
  defaultFromName: z.string().optional(),
  twilioAccountSid: z.string().optional(),
  twilioAuthToken: z.string().optional(),
  twilioMessagingServiceSid: z.string().optional(),
  askAiBubbleEnabled: z.boolean().optional(),
  a2pBrandStatus: z.enum(['NOT_STARTED', 'IN_PROGRESS', 'APPROVED', 'REJECTED']),
  a2pCampaignStatus: z.enum(['NOT_STARTED', 'IN_PROGRESS', 'APPROVED', 'REJECTED']),
  planName: z.string().optional(),
  billingStatus: z.enum(['TRIALING', 'PAID', 'PAST_DUE', 'CANCELLED']),
  monthlyPriceCents: z.coerce.number().min(0),
  currentPeriodEndsAt: z.string().optional(),
  notes: z.string().optional(),
});

function isLegacyRedAccent(color = '') {
  return ['#EF1111', '#E5092C', '#D60024', '#B60020', '#FF2A45', '#A6001B'].includes(String(color).trim().toUpperCase());
}

const aiConfigSchema = z.object({
  provider: z.enum(['openai', 'claude']),
  model: z.string().optional(),
  openaiApiKey: z.string().optional(),
  anthropicApiKey: z.string().optional(),
  clearOpenAIKey: z.boolean().optional(),
  clearClaudeKey: z.boolean().optional(),
});

// ── Field wrapper ─────────────────────────────────────────────────────────────
function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--textMuted)' }}>{label}</label>
      {children}
      {error && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
}

function SectionCard({ title, description, children, tone = 'default' }) {
  const toneStyle = tone === 'danger'
    ? { background: 'rgba(239,17,17,.08)', border: '1px solid rgba(239,17,17,.28)' }
    : tone === 'soft'
      ? { background: 'var(--accentSoft)', border: '1px solid var(--border)' }
      : { background: 'var(--card)', border: '1px solid var(--border)' };

  return (
    <section className="rounded-xl p-4 space-y-4" style={toneStyle}>
      <div>
        <p className="text-sm font-semibold" style={{ color: tone === 'danger' ? 'var(--danger)' : 'var(--text)' }}>{title}</p>
        {description && <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>{description}</p>}
      </div>
      {children}
    </section>
  );
}

function StatusPill({ value }) {
  const map = {
    ACTIVE: ['Active', 'rgba(34,201,122,.12)', 'var(--success)'],
    TRIAL: ['Trial', 'rgba(59,167,255,.12)', 'var(--info)'],
    PAST_DUE: ['Past due', 'rgba(245,166,35,.12)', 'var(--warning)'],
    SUSPENDED: ['Suspended', 'rgba(239,17,17,.14)', 'var(--danger)'],
    CANCELLED: ['Cancelled', 'var(--surface)', 'var(--textMuted)'],
    VERIFIED: ['Verified', 'rgba(34,201,122,.12)', 'var(--success)'],
    PENDING: ['Pending', 'rgba(245,166,35,.12)', 'var(--warning)'],
    FAILED: ['Not verified', 'rgba(239,17,17,.14)', 'var(--danger)'],
    PAID: ['Paid', 'rgba(34,201,122,.12)', 'var(--success)'],
    TRIALING: ['Trialing', 'rgba(59,167,255,.12)', 'var(--info)'],
    APPROVED: ['Approved', 'rgba(34,201,122,.12)', 'var(--success)'],
    IN_PROGRESS: ['In progress', 'rgba(245,166,35,.12)', 'var(--warning)'],
    REJECTED: ['Rejected', 'rgba(239,17,17,.14)', 'var(--danger)'],
    NOT_STARTED: ['Not started', 'var(--surface)', 'var(--textMuted)'],
  };
  const [label, bg, color] = map[value] || map.NOT_STARTED;
  return (
    <span className="inline-flex px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: bg, color, border: '1px solid var(--border)' }}>
      {label}
    </span>
  );
}

function CloseAccountPanel({ workspace }) {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const closeAccount = async () => {
    if (!workspace?.name) return;
    if (confirmation !== workspace.name && confirmation !== workspace.customerCode) {
      toast.error(`Type ${workspace.name} or ${workspace.customerCode} to confirm`);
      return;
    }
    if (!password) {
      toast.error('Enter your password');
      return;
    }
    if (!confirm('This permanently deletes this workspace and all its data. Continue?')) return;

    setBusy(true);
    try {
      await settingsApi.closeWorkspace({ confirmation, password });
      toast.success('Workspace permanently deleted');
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Account deletion failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard
      title="Danger Zone"
      description="Permanently delete this workspace, users, contacts, tags, messages, campaigns, automations, API keys, Twilio numbers, webhooks, domains, and logs."
      tone="danger"
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={`Type ${workspace?.customerCode || 'customer code'} or workspace name`}>
          <input value={confirmation} onChange={e => setConfirmation(e.target.value)} className="input" placeholder={workspace?.name || 'Workspace name'} />
        </Field>
        <Field label="Your password">
          <input value={password} onChange={e => setPassword(e.target.value)} type="password" className="input" placeholder="Confirm password" />
        </Field>
      </div>
      <button type="button" onClick={closeAccount} disabled={busy} className="px-4 py-2 rounded-lg text-sm font-bold" style={{ background: 'var(--danger)', color: '#fff' }}>
        {busy ? 'Deleting...' : 'Delete Workspace Forever'}
      </button>
    </SectionCard>
  );
}

// ── WHITE LABEL TAB ──────────────────────────────────────────────────────────
export function WhiteLabelTab() {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domain, setDomain] = useState('');
  const [domainBusyId, setDomainBusyId] = useState(null);
  const setTenant = useAuthStore((s) => s.setTenant);
  const { updateTheme } = useTheme();
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(workspaceSchema),
    defaultValues: {
      name: '',
      brandName: '',
      ownerEmail: '',
      supportEmail: '',
      logoUrl: '',
      primaryColor: '#5557E8',
      platformSubdomain: '',
      primaryDomain: '',
      defaultFromName: '',
      twilioAccountSid: '',
      twilioAuthToken: '',
      twilioMessagingServiceSid: '',
      a2pBrandStatus: 'NOT_STARTED',
      a2pCampaignStatus: 'NOT_STARTED',
      planName: 'Bulk SMS Starter',
      billingStatus: 'PAID',
      monthlyPriceCents: 16000,
      currentPeriodEndsAt: '',
      notes: '',
    },
  });

  const formValues = (data = {}) => ({
    ...data,
    ownerEmail: data.ownerEmail || '',
    supportEmail: data.supportEmail || '',
    logoUrl: data.logoUrl || '',
    platformSubdomain: data.platformSubdomain || '',
    primaryDomain: data.primaryDomain || '',
    defaultFromName: data.defaultFromName || '',
    twilioAccountSid: data.twilioAccountSid || '',
    twilioAuthToken: data.twilioAuthToken || '',
    twilioMessagingServiceSid: data.twilioMessagingServiceSid || '',
    planName: data.planName || 'Bulk SMS Starter',
    billingStatus: data.billingStatus || 'PAID',
    monthlyPriceCents: data.monthlyPriceCents ?? 16000,
    currentPeriodEndsAt: data.currentPeriodEndsAt || '',
    notes: data.notes || '',
  });

  const load = () => {
    setLoading(true);
    settingsApi.getWorkspace()
      .then(({ data }) => {
        setWorkspace(data);
        setTenant(data);
        updateTheme({ customAccent: data.primaryColor && !isLegacyRedAccent(data.primaryColor) ? data.primaryColor : '' });
        reset(formValues(data));
      })
      .catch(() => toast.error('Failed to load workspace'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [reset]);

  const save = async (values) => {
    setSaving(true);
    try {
      const { data } = await settingsApi.updateWorkspace(values);
      setWorkspace(data);
      setTenant(data);
      updateTheme({ customAccent: data.primaryColor && !isLegacyRedAccent(data.primaryColor) ? data.primaryColor : '' });
      reset(formValues(data));
      toast.success('Workspace settings saved');
    } catch {
      toast.error('Failed to save workspace');
    } finally {
      setSaving(false);
    }
  };

  const addDomain = async () => {
    if (!domain.trim()) return toast.error('Enter a domain');
    try {
      const { data } = await settingsApi.addCustomDomain({ domain: domain.trim(), isPrimary: !workspace?.customDomains?.length });
      setWorkspace((prev) => ({ ...prev, customDomains: [...(prev?.customDomains || []), data] }));
      setDomain('');
      toast.success('Domain added — add the CNAME record below, then click Check DNS');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add domain');
    }
  };

  const verifyDomain = async (id) => {
    setDomainBusyId(id);
    try {
      const { data } = await settingsApi.verifyCustomDomain(id);
      setWorkspace((prev) => ({
        ...prev,
        customDomains: (prev?.customDomains || []).map((d) => (d.id === id ? data : d)),
      }));
      if (data.status === 'VERIFIED') toast.success(data.message || 'Domain verified');
      else toast.error(data.message || 'Not verified yet');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not check this domain');
    } finally {
      setDomainBusyId(null);
    }
  };

  const removeDomain = async (id) => {
    if (!confirm('Remove this domain? Links and webhooks will fall back to the default platform domain.')) return;
    setDomainBusyId(id);
    try {
      await settingsApi.deleteCustomDomain(id);
      setWorkspace((prev) => ({ ...prev, customDomains: (prev?.customDomains || []).filter((d) => d.id !== id) }));
      toast.success('Domain removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove domain');
    } finally {
      setDomainBusyId(null);
    }
  };

  if (loading) return <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Loading workspace...</p>;

  return (
    <div className="space-y-5">
      <div className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Workspace Overview</p>
            <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>
              This workspace has private contacts, campaigns, automations, Twilio setup, domains, and team access.
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <StatusPill value={workspace?.status || 'ACTIVE'} />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(save)} className="space-y-5">
        <SectionCard title="Business Details" description="The name, email, logo, and color customers see inside this workspace.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Business/workspace name" error={errors.name?.message}><input {...register('name')} className="input" /></Field>
            <Field label="Display brand name" error={errors.brandName?.message}><input {...register('brandName')} className="input" /></Field>
            <Field label="Owner email" error={errors.ownerEmail?.message}><input {...register('ownerEmail')} className="input" /></Field>
            <Field label="Support email shown to users" error={errors.supportEmail?.message}><input {...register('supportEmail')} className="input" /></Field>
            <Field label="Logo URL" error={errors.logoUrl?.message}><input {...register('logoUrl')} className="input" placeholder="https://client-domain.com/logo.png" /></Field>
            <Field label="Main brand color" error={errors.primaryColor?.message}><input {...register('primaryColor')} type="color" className="h-10 w-full rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Login & Domains" description="Where this customer signs in. A verified custom domain replaces the platform's own domain in every short link and automation webhook URL sent to their contacts.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Workspace short name" error={errors.platformSubdomain?.message}><input {...register('platformSubdomain')} className="input" placeholder="clientname" /></Field>
            <Field label="Primary custom domain (display only)" error={errors.primaryDomain?.message}><input {...register('primaryDomain')} className="input" placeholder="sms.clientdomain.com" /></Field>
          </div>

          <div className="rounded-lg p-3 flex items-start gap-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <IconInfo size={14} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} />
            <p className="text-[11px]" style={{ color: 'var(--textMuted)' }}>
              Add your domain below, point a CNAME at it from your DNS provider, then click Check DNS. Once it shows Verified, short links and automation webhook URLs switch to your domain automatically — no further setup. SSL activation on our side typically follows within a few hours of verification.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <input value={domain} onChange={e => setDomain(e.target.value)} className="input" placeholder="sms.clientdomain.com" />
            <button type="button" onClick={addDomain} className="btn-secondary text-sm flex-shrink-0">Add Domain</button>
          </div>

          <div className="space-y-3">
            {(workspace?.customDomains || []).length === 0 && (
              <p className="text-xs" style={{ color: 'var(--textDim)' }}>No custom domain added yet — links currently use the platform's own domain.</p>
            )}
            {(workspace?.customDomains || []).map((d) => (
              <div key={d.id} className="rounded-lg p-3 space-y-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{d.domain}</p>
                    <StatusPill value={d.status} />
                    {d.isPrimary && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--textDim)' }}>Primary</span>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => verifyDomain(d.id)}
                      disabled={domainBusyId === d.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-medium"
                      style={{ background: 'var(--card)', color: 'var(--textMuted)', border: '1px solid var(--border)' }}
                    >
                      <IconRefresh size={12} /> {domainBusyId === d.id ? 'Checking...' : 'Check DNS'}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeDomain(d.id)}
                      disabled={domainBusyId === d.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-medium"
                      style={{ background: 'var(--card)', color: 'var(--danger)', border: '1px solid var(--border)' }}
                    >
                      <IconTrash size={12} /> Remove
                    </button>
                  </div>
                </div>
                {d.message && (
                  <p className="text-[11px]" style={{ color: d.status === 'VERIFIED' ? 'var(--success)' : 'var(--textMuted)' }}>{d.message}</p>
                )}
                <CopyRow label="CNAME host" value={d.verificationName} />
                <CopyRow label="CNAME points to" value={d.verificationValue} />
              </div>
            ))}
          </div>
        </SectionCard>

        <Field label="Private admin notes" error={errors.notes?.message}><textarea {...register('notes')} rows={3} className="input resize-none" /></Field>

        <button type="submit" disabled={saving} className="btn-primary text-sm">
          {saving ? 'Saving...' : 'Save Business Settings'}
        </button>
      </form>
      <CloseAccountPanel workspace={workspace} />
    </div>
  );
}

// ── A2P APPROVAL TAB ─────────────────────────────────────────────────────────
const EMPTY_A2P = {
  businessProfile: {
    legalName: '',
    brandName: '',
    businessType: '',
    ein: '',
    websiteUrl: '',
    supportEmail: '',
    supportPhone: '',
    privacyPolicyUrl: '',
    termsUrl: '',
    businessAddress: '',
  },
  optInSetup: {
    programName: '',
    useCase: '',
    messageFrequency: '',
    sampleMessage: '',
    optInKeywords: '',
    optOutKeywords: '',
    helpKeywords: '',
    consentText: '',
    privacyText: '',
    termsText: '',
    automationId: '',
    automationWebhookUrl: '',
    formTitle: '',
    submitLabel: '',
    successMessage: '',
  },
};

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function hasWebhookTrigger(automation) {
  return automation?.flow?.nodes?.some((node) => node.data?.nodeType === 'webhook');
}

function publicAutomationWebhookUrl(automation) {
  if (!automation?.id || automation.status !== 'ACTIVE') return '';
  return `${window.location.origin}/api/automations/${automation.id}/webhook`;
}

function a2pMessageFlow(profile, setup) {
  const brand = profile.brandName || profile.legalName || 'the business';
  const site = profile.websiteUrl || 'the business website';
  const privacy = profile.privacyPolicyUrl || 'the public privacy policy URL';
  const terms = profile.termsUrl || 'the public terms URL';
  return `End users opt in by visiting ${site} and submitting a form for ${brand}. The form has an optional, unchecked SMS consent checkbox with this disclosure: "${setup.consentText}". Form submissions are stored in the CRM with the phone number, name, email, source, timestamp, consent text, Privacy Policy URL (${privacy}), and Terms and Conditions URL (${terms}). Users can opt out by replying ${setup.optOutKeywords || 'STOP'} and can request help by replying ${setup.helpKeywords || 'HELP'}.`;
}

function consentEmbed(profile, setup) {
  const privacy = profile.privacyPolicyUrl || '#privacy';
  const terms = profile.termsUrl || '#terms';
  return `<label style="display:flex;gap:10px;align-items:flex-start;font:14px/1.45 system-ui,-apple-system,Segoe UI,sans-serif;color:#111;">
  <input type="checkbox" name="sms_consent" value="yes" required style="margin-top:3px;">
  <span>
    ${escapeHtml(setup.consentText)}
    <a href="${escapeHtml(privacy)}" target="_blank" rel="noopener">Privacy Policy</a>
    and
    <a href="${escapeHtml(terms)}" target="_blank" rel="noopener">Terms</a>.
  </span>
</label>`;
}

function fullFormEmbed(profile, setup) {
  const action = setup.automationWebhookUrl || 'PASTE_PUBLISHED_AUTOMATION_WEBHOOK_URL_HERE';
  const title = setup.formTitle || `Get SMS updates from ${profile.brandName || 'us'}`;
  const submit = setup.submitLabel || 'Opt in';
  return `<form action="${escapeHtml(action)}" method="POST" style="max-width:420px;width:100%;display:grid;gap:12px;font:14px system-ui,-apple-system,Segoe UI,sans-serif;">
  <h3 style="margin:0;font-size:20px;">${escapeHtml(title)}</h3>
  <input name="name" placeholder="Full name" autocomplete="name" style="padding:12px;border:1px solid #ddd;border-radius:8px;">
  <input name="phone" placeholder="Mobile phone" autocomplete="tel" required style="padding:12px;border:1px solid #ddd;border-radius:8px;">
  <input name="email" placeholder="Email" autocomplete="email" style="padding:12px;border:1px solid #ddd;border-radius:8px;">
  <input type="hidden" name="source" value="website_opt_in">
  <input type="hidden" name="consent_text" value="${escapeHtml(setup.consentText)}">
  ${consentEmbed(profile, setup)}
  <button type="submit" style="padding:12px 16px;border:0;border-radius:8px;background:#111;color:#fff;font-weight:700;">${escapeHtml(submit)}</button>
</form>`;
}

function policyStarterPage(profile, setup, type) {
  const brand = profile.brandName || profile.legalName || 'Our business';
  if (type === 'privacy') {
    return `<section style="font:16px/1.6 system-ui,-apple-system,Segoe UI,sans-serif;max-width:760px;margin:auto;padding:24px;">
  <h1>${escapeHtml(brand)} Privacy Policy</h1>
  <p>${escapeHtml(setup.privacyText)}</p>
  <p>SMS message frequency: ${escapeHtml(setup.messageFrequency || 'Message frequency varies.')} Message and data rates may apply.</p>
  <p>Reply STOP to unsubscribe. Reply HELP for help.</p>
</section>`;
  }
  return `<section style="font:16px/1.6 system-ui,-apple-system,Segoe UI,sans-serif;max-width:760px;margin:auto;padding:24px;">
  <h1>${escapeHtml(brand)} SMS Terms</h1>
  <p>${escapeHtml(setup.termsText)}</p>
  <p>Message frequency: ${escapeHtml(setup.messageFrequency || 'Message frequency varies.')} Message and data rates may apply. Carriers are not liable for delayed or undelivered messages.</p>
  <p>Reply STOP to unsubscribe. Reply HELP for help.</p>
</section>`;
}

export function A2pApprovalTab() {
  const [kit, setKit] = useState(EMPTY_A2P);
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const profile = kit.businessProfile || EMPTY_A2P.businessProfile;
  const setup = kit.optInSetup || EMPTY_A2P.optInSetup;

  const load = () => {
    setLoading(true);
    Promise.all([
      settingsApi.getA2pKit(),
      automationsApi.list(),
    ])
      .then(([kitRes, automationRes]) => {
        setKit({
          businessProfile: { ...EMPTY_A2P.businessProfile, ...(kitRes.data.businessProfile || {}) },
          optInSetup: { ...EMPTY_A2P.optInSetup, ...(kitRes.data.optInSetup || {}) },
          statuses: kitRes.data.statuses || {},
        });
        setAutomations(automationRes.data || []);
      })
      .catch(() => toast.error('Failed to load A2P approval kit'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const updateProfile = (key, value) => setKit((current) => ({
    ...current,
    businessProfile: { ...(current.businessProfile || {}), [key]: value },
  }));

  const updateSetup = (key, value) => setKit((current) => ({
    ...current,
    optInSetup: { ...(current.optInSetup || {}), [key]: value },
  }));

  const chooseAutomation = (id) => {
    const automation = automations.find((item) => item.id === id);
    updateSetup('automationId', id);
    updateSetup('automationWebhookUrl', publicAutomationWebhookUrl(automation));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await settingsApi.updateA2pKit({
        businessProfile: profile,
        optInSetup: setup,
      });
      setKit({
        businessProfile: { ...EMPTY_A2P.businessProfile, ...(data.businessProfile || {}) },
        optInSetup: { ...EMPTY_A2P.optInSetup, ...(data.optInSetup || {}) },
        statuses: data.statuses || {},
      });
      toast.success('A2P approval kit saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save A2P approval kit');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Loading A2P approval kit...</p>;

  const webhookAutomations = automations.filter(hasWebhookTrigger);
  const rules = [
    ['Optional checkbox', 'The SMS checkbox should be separate, unchecked by default, and not required for buying or using the website.'],
    ['Clear sender', `The form names ${profile.brandName || profile.legalName || 'the business'} as the sender.`],
    ['Proof of consent', 'Webhook submissions should save phone, source, consent text, timestamp, and policy links.'],
    ['Opt-out language', `Every page and sample message should include ${setup.optOutKeywords || 'STOP'} and ${setup.helpKeywords || 'HELP'}.`],
    ['Public links', 'Privacy Policy and Terms pages must be public and not behind login.'],
  ];

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-3 gap-3">
        <SectionCard title="Brand Status" description="Internal A2P brand registration progress."><StatusPill value={kit.statuses?.brand || 'NOT_STARTED'} /></SectionCard>
        <SectionCard title="Campaign Status" description="Internal campaign approval progress."><StatusPill value={kit.statuses?.campaign || 'NOT_STARTED'} /></SectionCard>
        <SectionCard title="Approval Readiness" description="Privacy, terms, consent text, and proof of opt-in.">
          <p className="text-2xl font-bold" style={{ color: profile.privacyPolicyUrl && profile.termsUrl && setup.consentText ? 'var(--success)' : 'var(--warning)' }}>
            {profile.privacyPolicyUrl && profile.termsUrl && setup.consentText ? 'Ready' : 'Needs links'}
          </p>
        </SectionCard>
      </div>

      <SectionCard title="Already Approved in Twilio?" description="If the customer already has A2P 10DLC approval, you do not need to rebuild the application here. Connect the approved Twilio setup, then keep this kit as proof and reference notes.">
        <div className="grid md:grid-cols-3 gap-3">
          {[
            ['1. Connect Twilio', 'Go to Phone & Twilio, paste the Account SID/Auth Token and the approved Messaging Service SID.'],
            ['2. Mark status', 'Set A2P brand and campaign status to Approved after confirming it in Twilio Trust Hub/Messaging.'],
            ['3. Send with confidence', 'Bulk campaigns will use the saved Messaging Service/default number, or the exact sender selected in the campaign composer.'],
          ].map(([title, body]) => (
            <div key={title} className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <p className="text-xs font-bold" style={{ color: 'var(--text)' }}>{title}</p>
              <p className="text-[11px] mt-1" style={{ color: 'var(--textMuted)' }}>{body}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Business Details for A2P" description="Use the customer's real legal details. These placeholders come from the workspace and can be edited before submission.">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Legal business name"><input className="input" value={profile.legalName} onChange={e => updateProfile('legalName', e.target.value)} /></Field>
          <Field label="Public brand name"><input className="input" value={profile.brandName} onChange={e => updateProfile('brandName', e.target.value)} /></Field>
          <Field label="Business type"><input className="input" value={profile.businessType} onChange={e => updateProfile('businessType', e.target.value)} placeholder="LLC, Corporation, Sole proprietor" /></Field>
          <Field label="EIN / tax ID"><input className="input" value={profile.ein} onChange={e => updateProfile('ein', e.target.value)} placeholder="Optional for prep notes" /></Field>
          <Field label="Website URL"><input className="input" value={profile.websiteUrl} onChange={e => updateProfile('websiteUrl', e.target.value)} placeholder="https://clientdomain.com" /></Field>
          <Field label="Support email"><input className="input" value={profile.supportEmail} onChange={e => updateProfile('supportEmail', e.target.value)} /></Field>
          <Field label="Support phone"><input className="input" value={profile.supportPhone} onChange={e => updateProfile('supportPhone', e.target.value)} /></Field>
          <Field label="Business address"><input className="input" value={profile.businessAddress} onChange={e => updateProfile('businessAddress', e.target.value)} /></Field>
          <Field label="Privacy Policy URL"><input className="input" value={profile.privacyPolicyUrl} onChange={e => updateProfile('privacyPolicyUrl', e.target.value)} placeholder="https://clientdomain.com/privacy" /></Field>
          <Field label="Terms URL"><input className="input" value={profile.termsUrl} onChange={e => updateProfile('termsUrl', e.target.value)} placeholder="https://clientdomain.com/terms" /></Field>
        </div>
      </SectionCard>

      <SectionCard title="SMS Consent & Campaign Copy" description="Approval-friendly wording for message flow, samples, website checkbox, policy pages, and Twilio review notes.">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="SMS program name"><input className="input" value={setup.programName} onChange={e => updateSetup('programName', e.target.value)} /></Field>
          <Field label="Message frequency"><input className="input" value={setup.messageFrequency} onChange={e => updateSetup('messageFrequency', e.target.value)} placeholder="Up to 4 messages per month." /></Field>
          <Field label="Opt-in keywords"><input className="input" value={setup.optInKeywords} onChange={e => updateSetup('optInKeywords', e.target.value)} /></Field>
          <Field label="Opt-out keywords"><input className="input" value={setup.optOutKeywords} onChange={e => updateSetup('optOutKeywords', e.target.value)} /></Field>
          <Field label="Help keywords"><input className="input" value={setup.helpKeywords} onChange={e => updateSetup('helpKeywords', e.target.value)} /></Field>
          <Field label="Form submit button"><input className="input" value={setup.submitLabel} onChange={e => updateSetup('submitLabel', e.target.value)} /></Field>
        </div>
        <Field label="Campaign use case"><textarea className="input resize-none" rows={3} value={setup.useCase} onChange={e => updateSetup('useCase', e.target.value)} /></Field>
        <Field label="Website checkbox consent text"><textarea className="input resize-none" rows={4} value={setup.consentText} onChange={e => updateSetup('consentText', e.target.value)} /></Field>
        <Field label="Sample SMS"><textarea className="input resize-none" rows={3} value={setup.sampleMessage} onChange={e => updateSetup('sampleMessage', e.target.value)} /></Field>
        <Field label="Privacy policy SMS paragraph"><textarea className="input resize-none" rows={3} value={setup.privacyText} onChange={e => updateSetup('privacyText', e.target.value)} /></Field>
        <Field label="Terms SMS paragraph"><textarea className="input resize-none" rows={3} value={setup.termsText} onChange={e => updateSetup('termsText', e.target.value)} /></Field>
      </SectionCard>

      <SectionCard title="Opt-in Form Automation" description="Choose the published webhook automation that should receive website opt-ins. Use a Form Submitted/Webhook trigger in Automations, then map phone, name, email, consent text, and source.">
        <div className="grid sm:grid-cols-[1fr_auto] gap-3">
          <Field label="Published form automation">
            <select className="input" value={setup.automationId} onChange={e => chooseAutomation(e.target.value)}>
              <option value="">Choose an automation</option>
              {webhookAutomations.map((automation) => (
                <option key={automation.id} value={automation.id}>
                  {automation.name} {automation.status === 'ACTIVE' ? '(live)' : '(draft)'}
                </option>
              ))}
            </select>
          </Field>
          <button type="button" className="btn-secondary self-end" onClick={() => copyText(setup.automationWebhookUrl || '')} disabled={!setup.automationWebhookUrl}>Copy URL</button>
        </div>
        <Field label="Automation webhook URL">
          <input className="input font-mono text-xs" value={setup.automationWebhookUrl} onChange={e => updateSetup('automationWebhookUrl', e.target.value)} placeholder="Publish a webhook automation, then select it here" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          {rules.map(([title, body]) => (
            <div key={title} className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <p className="text-xs font-bold" style={{ color: 'var(--text)' }}>{title}</p>
              <p className="text-[11px] mt-1" style={{ color: 'var(--textMuted)' }}>{body}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Copy for Twilio Review" description="Paste this into campaign review notes/message flow, then adjust anything specific to the customer's real opt-in flow.">
        <CopyRow label="Message flow / opt-in explanation" value={a2pMessageFlow(profile, setup)} />
        <CopyRow label="Sample message" value={setup.sampleMessage} />
        <CopyRow label="Consent checkbox only" value={consentEmbed(profile, setup)} />
        <CopyRow label="Full responsive opt-in form" value={fullFormEmbed(profile, setup)} />
        <CopyRow label="Privacy page starter block" value={policyStarterPage(profile, setup, 'privacy')} />
        <CopyRow label="Terms page starter block" value={policyStarterPage(profile, setup, 'terms')} />
      </SectionCard>

      <div className="flex flex-col sm:flex-row gap-2">
        <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm">
          {saving ? 'Saving...' : 'Save A2P Kit'}
        </button>
        <button type="button" onClick={load} className="btn-secondary text-sm">Reload</button>
      </div>
    </div>
  );
}

// ── WEBHOOKS TAB ──────────────────────────────────────────────────────────────
export function WebhooksTab() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    settingsApi.getWebhookInfo()
      .then(({ data }) => setInfo(data))
      .catch(() => toast.error('Failed to load webhook info'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: 'var(--textMuted)' }}>Loading…</p>;
  if (!info) return null;

  return (
    <div className="space-y-5">
      {/* Instructions */}
      <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--accentSoft)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>Webhook Setup Guide</p>
        <div className="space-y-2 text-xs" style={{ color: 'var(--textMuted)' }}>
          <p><span className="font-semibold" style={{ color: 'var(--success)' }}>SMS: </span>{info.instructions.sms}</p>
          <p><span className="font-semibold" style={{ color: 'var(--success)' }}>WhatsApp: </span>{info.instructions.whatsapp}</p>
        </div>
      </div>
      <div className="rounded-xl px-4 py-3" style={{ background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.2)' }}>
        <p className="text-xs" style={{ color: 'var(--warning)' }}>⚡ {info.instructions.note}</p>
      </div>

      {/* URL groups */}
      {info.webhooks.map((wh, i) => (
        <div key={i} className="rounded-xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
            <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>{wh.label}</span>
            <code className="text-[10px] px-2 py-0.5 rounded-lg font-mono" style={{ background: 'var(--card)', color: 'var(--textMuted)', border: '1px solid var(--border)' }}>
              {wh.baseUrl}
            </code>
          </div>
          <div className="px-4">
            <CopyRow label="Inbound SMS URL" value={wh.inboundSms} />
            <CopyRow label="Inbound WhatsApp URL" value={wh.inboundWhatsapp} />
            <CopyRow label="Delivery status callback URL" value={wh.statusCallback} />
            <CopyRow label="External sending API URL" value={wh.externalOutbound} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ConnectionTile({ title, description, status, statusTone = 'default', children, actions }) {
  const toneMap = {
    good: ['rgba(34,201,122,.12)', 'var(--success)'],
    warn: ['rgba(245,166,35,.12)', 'var(--warning)'],
    danger: ['rgba(239,17,17,.14)', 'var(--danger)'],
    default: ['var(--surface)', 'var(--textMuted)'],
  };
  const [bg, color] = toneMap[statusTone] || toneMap.default;

  return (
    <section className="rounded-xl p-4 space-y-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold" style={{ color: 'var(--text)' }}>{title}</h3>
            {status && (
              <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1" style={{ background: bg, color, border: '1px solid var(--border)' }}>
                {status}
              </span>
            )}
          </div>
          {description && <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--textMuted)' }}>{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2 sm:justify-end flex-shrink-0">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

function MiniCopy({ label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--textDim)' }}>{label}</p>
        <button type="button" onClick={() => copyText(value)} className="text-[10px] font-bold" style={{ color: 'var(--accent)' }}>Copy</button>
      </div>
      <code className="block text-xs font-mono break-anywhere" style={{ color: 'var(--textMuted)' }}>{value}</code>
    </div>
  );
}

export function ConnectionsHubTab({ setTab }) {
  const navigate = useNavigate();
  const tenant = useAuthStore((s) => s.tenant);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [waStatus, setWaStatus] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [hooks, ws, keys, shortLinks] = await Promise.all([
        settingsApi.getWebhookInfo().catch(() => ({ data: null })),
        settingsApi.getWorkspace().catch(() => ({ data: null })),
        settingsApi.listApiKeys().catch(() => ({ data: [] })),
        linksApi.list().catch(() => ({ data: [] })),
      ]);
      setWebhookInfo(hooks.data);
      setWorkspace(ws.data);
      setApiKeys(keys.data || []);
      setLinks(shortLinks.data || []);
      if (tenant?.whatsappWebEnabled) {
        const status = await whatsappWebApi.status().catch(() => ({ data: null }));
        setWaStatus(status.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenant?.whatsappWebEnabled]);

  const primaryWebhook = webhookInfo?.webhooks?.[0];
  const externalWebhookHint = `${window.location.origin}/api/automations/webhook/{published-workflow-token}`;
  const mcpUrl = `${window.location.origin}/api/mcp`;
  const apiBase = `${window.location.origin}/api`;
  const activeKeys = apiKeys.filter((key) => key.isActive).length;
  const twilioReady = Boolean(workspace?.twilioAccountSid && workspace?.twilioAuthTokenStatus === 'saved');
  const waReady = waStatus?.ready || waStatus?.status === 'CONNECTED';
  const emailReady = Boolean(workspace?.emailDefaultFromEmail);

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: 'var(--textMuted)' }}>Loading connections...</p>;

  return (
    <div className="space-y-5">
      <SectionCard
        title="Connection Center"
        description="Everything included in this workspace is here first: setup links, active modules, API access, short links, and automation endpoints."
        tone="soft"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ...(hasSms(tenant) || hasCalls(tenant) ? [['Twilio', twilioReady ? 'Ready' : 'Needs setup', twilioReady ? 'good' : 'warn']] : []),
            ...(hasDirectWhatsApp(tenant) ? [['Direct WhatsApp', waReady ? 'Connected' : 'Enabled', waReady ? 'good' : 'warn']] : []),
            ...(hasEmail(tenant) ? [['Email', emailReady ? 'Ready' : 'Needs setup', emailReady ? 'good' : 'warn']] : []),
            ...(hasApiAccess(tenant) ? [['API Keys', `${activeKeys} active`, activeKeys ? 'good' : 'warn']] : []),
            ...(hasShortLinks(tenant) ? [['Short Links', `${links.length} saved`, links.length ? 'good' : 'default']] : []),
          ].map(([label, status, tone]) => (
            <div key={label} className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--textDim)' }}>{label}</p>
              <p className="text-sm font-bold mt-1" style={{ color: tone === 'good' ? 'var(--success)' : tone === 'warn' ? 'var(--warning)' : 'var(--text)' }}>{status}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="grid lg:grid-cols-2 gap-4">
        {(hasSms(tenant) || hasCalls(tenant)) && <ConnectionTile
          title="Twilio SMS, Twilio WhatsApp & Voice Webhooks"
          description="Use this for Twilio phone numbers, SMS campaigns, Twilio WhatsApp sender numbers, delivery callbacks, and voice/IVR webhooks."
          status={twilioReady ? 'ready' : 'setup needed'}
          statusTone={twilioReady ? 'good' : 'warn'}
          actions={<button type="button" className="btn-primary text-xs" onClick={() => setTab('numbers')}>Open Phone & Twilio</button>}
        >
          <div className="grid gap-2">
            <MiniCopy label="Inbound SMS / Twilio replies" value={primaryWebhook?.inboundSms} />
            <MiniCopy label="Delivery status callback" value={primaryWebhook?.statusCallback} />
            <MiniCopy label="External outbound API" value={primaryWebhook?.externalOutbound} />
          </div>
        </ConnectionTile>}

        {hasDirectWhatsApp(tenant) && <ConnectionTile
          title="Direct WhatsApp Module"
          description="Use this for a connected WhatsApp account, API-key sending, synced WhatsApp inbox, and forwarding inbound replies to your own endpoint. This is separate from Twilio WhatsApp."
          status={waReady ? 'connected' : 'enabled'}
          statusTone={waReady ? 'good' : 'warn'}
          actions={<button type="button" className="btn-primary text-xs" onClick={() => setTab('directwa')}>Configure Direct WhatsApp</button>}
        >
          <div className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="text-xs font-bold" style={{ color: 'var(--text)' }}>{waStatus?.account?.phone || waStatus?.persisted?.account?.phone || 'No WhatsApp account linked yet'}</p>
            <p className="text-[11px] mt-1" style={{ color: 'var(--textMuted)' }}>
              Direct WhatsApp is separate from Twilio SMS webhooks. Use it when the customer wants WhatsApp Web style chat sync and direct WhatsApp sending.
            </p>
          </div>
        </ConnectionTile>}

        {hasEmail(tenant) && <ConnectionTile
          title="Email Marketing"
          description="Set a verified sending domain, default sender, tracking webhooks, and suppression-safe email campaigns."
          status={emailReady ? 'ready' : 'setup needed'}
          statusTone={emailReady ? 'good' : 'warn'}
          actions={<button type="button" className="btn-primary text-xs" onClick={() => setTab('email')}>Configure Email</button>}
        >
          <div className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="text-xs font-bold" style={{ color: 'var(--text)' }}>{workspace?.emailDefaultFromEmail || 'No sender email configured yet'}</p>
            <p className="text-[11px] mt-1" style={{ color: 'var(--textMuted)' }}>
              Email replies, campaigns, opens, clicks, bounces, complaints, and unsubscribes stay inside this workspace.
            </p>
          </div>
        </ConnectionTile>}

        {hasAutomation(tenant) && <ConnectionTile
          title="External Form/List Webhooks"
          description="Use this for Wix forms, website forms, Zapier, n8n, or any app that POSTs contacts/list data into an automation."
          status="automation endpoint"
          statusTone="good"
          actions={<button type="button" className="btn-primary text-xs" onClick={() => navigate('/automations')}>Build Webhook Flow</button>}
        >
          <div className="space-y-2">
            <MiniCopy label="URL format after publishing" value={externalWebhookHint} />
            <div className="rounded-lg p-3 text-xs leading-relaxed" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--textMuted)' }}>
              Create an automation with <b>External Data Endpoint</b>, publish it, then copy the generated URL from the automation header. Connect it to <b>Import Contacts From Webhook</b>, <b>Add Tag</b>, <b>Send SMS</b>, or any follow-up steps.
            </div>
          </div>
        </ConnectionTile>}

        {hasApiAccess(tenant) && <ConnectionTile
          title="API Keys & Custom Integrations"
          description="Create keys for developer API access, Zapier/n8n HTTP requests, website backends, and private tools."
          status={activeKeys ? `${activeKeys} active` : 'no key yet'}
          statusTone={activeKeys ? 'good' : 'warn'}
          actions={<button type="button" className="btn-primary text-xs" onClick={() => setTab('apikeys')}>Manage API Keys</button>}
        >
          <div className="grid gap-2">
            <MiniCopy label="API base URL" value={apiBase} />
            <MiniCopy label="External outbound endpoint" value={primaryWebhook?.externalOutbound} />
          </div>
        </ConnectionTile>}

        {hasAutomation(tenant) && <ConnectionTile
          title="AI Agent / MCP Connection"
          description="Connect ChatGPT, Claude, n8n, or custom AI agents with scoped permissions for this one workspace."
          status="scoped access"
          statusTone="good"
          actions={<button type="button" className="btn-primary text-xs" onClick={() => setTab('agents')}>Open AI Assistants</button>}
        >
          <MiniCopy label="MCP endpoint" value={mcpUrl} />
        </ConnectionTile>}

        {hasShortLinks(tenant) && <ConnectionTile
          title="Short Links for SMS"
          description="Shorten WhatsApp group links, ticket pages, coupon pages, and event links so they work better inside SMS."
          status={links.length ? `${links.length} links` : 'create first link'}
          statusTone={links.length ? 'good' : 'default'}
          actions={<button type="button" className="btn-primary text-xs" onClick={() => setTab('shortlinks')}>Open Short Links</button>}
        >
          {links[0] ? <MiniCopy label="Latest short link" value={links[0].shortUrl} /> : (
            <p className="text-xs rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--textMuted)' }}>
              Create one short link and paste it into keyword replies, campaigns, or automation messages.
            </p>
          )}
        </ConnectionTile>}
      </div>
    </div>
  );
}

export function DirectWhatsAppApiTab() {
  const navigate = useNavigate();
  const tenant = useAuthStore((s) => s.tenant);
  const [settings, setSettings] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [replyWebhookUrl, setReplyWebhookUrl] = useState('');
  const [replyWebhookEnabled, setReplyWebhookEnabled] = useState(false);
  const [apiEnabled, setApiEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [settingsRes, keysRes] = await Promise.all([
        settingsApi.getDirectWhatsAppSettings(),
        settingsApi.listApiKeys().catch(() => ({ data: [] })),
      ]);
      setSettings(settingsRes.data);
      setApiEnabled(Boolean(settingsRes.data.apiEnabled));
      setReplyWebhookEnabled(Boolean(settingsRes.data.replyWebhookEnabled));
      setReplyWebhookUrl(settingsRes.data.replyWebhookUrl || '');
      setApiKeys(keysRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Direct WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await settingsApi.updateDirectWhatsAppSettings({
        whatsappWebApiEnabled: apiEnabled,
        whatsappWebReplyWebhookEnabled: replyWebhookEnabled,
        whatsappWebReplyWebhookUrl: replyWebhookUrl.trim(),
      });
      setSettings(data);
      toast.success('Direct WhatsApp settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save Direct WhatsApp settings');
    } finally {
      setSaving(false);
    }
  };

  const createKey = async () => {
    setCreatingKey(true);
    try {
      const { data } = await settingsApi.createApiKey('Direct WhatsApp API');
      setNewKey(data);
      const keys = await settingsApi.listApiKeys();
      setApiKeys(keys.data || []);
      toast.success('Direct WhatsApp API key created');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create API key');
    } finally {
      setCreatingKey(false);
    }
  };

  const statusTone = !settings?.enabled ? 'default' : settings?.status === 'CONNECTED' ? 'good' : 'warn';
  const activeKeys = apiKeys.filter((key) => key.isActive);
  const sampleBody = JSON.stringify(settings?.exampleBody || { to: '+14155550101', body: 'Hello from Direct WhatsApp API' }, null, 2);
  const replySample = JSON.stringify(settings?.replyPayloadExample || {}, null, 2);

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: 'var(--textMuted)' }}>Loading Direct WhatsApp...</p>;

  return (
    <div className="space-y-5">
      {!settings?.enabled && (
        <SectionCard
          title="Direct WhatsApp is owner-controlled"
          description="This feature only appears for customers after the platform owner activates Direct WhatsApp for the workspace."
          tone="soft"
        >
          <p className="text-sm" style={{ color: 'var(--textMuted)' }}>
            Activate it from your owner dashboard for the customer, then they can connect WhatsApp, use the API, and configure reply forwarding here.
          </p>
        </SectionCard>
      )}

      <ConnectionTile
        title="Direct WhatsApp Account"
        description="This is the connected WhatsApp account that sends API messages and receives replies."
        status={!settings?.enabled ? 'owner locked' : settings?.status || 'not connected'}
        statusTone={statusTone}
        actions={settings?.enabled && <button type="button" className="btn-primary text-xs" onClick={() => navigate('/whatsapp')}>Connect / Open WhatsApp</button>}
      >
        <div className="grid sm:grid-cols-2 gap-3">
          <MiniCopy label="Connected account" value={settings?.account?.phone || settings?.account?.name || 'Not connected yet'} />
          <MiniCopy label="Last connected" value={settings?.lastConnectedAt ? new Date(settings.lastConnectedAt).toLocaleString() : 'Never'} />
        </div>
        {settings?.lastError && (
          <p className="text-xs rounded-lg p-3" style={{ background: 'rgba(239,17,17,.1)', border: '1px solid rgba(239,17,17,.25)', color: 'var(--danger)' }}>
            {settings.lastError}
          </p>
        )}
      </ConnectionTile>

      <SectionCard title="Send WhatsApp by API" description="Use this endpoint from your website, CRM, n8n, Zapier, or any backend. It sends through the connected WhatsApp account, not Twilio.">
        <div className="space-y-3">
          <label className="flex items-center gap-3 text-sm font-bold" style={{ color: 'var(--text)' }}>
            <input type="checkbox" checked={apiEnabled} onChange={(e) => setApiEnabled(e.target.checked)} />
            Allow Direct WhatsApp API sending for this workspace
          </label>
          <MiniCopy label="POST endpoint" value={settings?.sendApiUrl} />
          <div className="grid lg:grid-cols-2 gap-3">
            <MiniCopy label="Required header" value="X-Api-Key: paste_workspace_api_key_here" />
            <MiniCopy label="JSON body" value={sampleBody} />
          </div>
          <div className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>API keys</p>
                <p className="text-xs" style={{ color: 'var(--textMuted)' }}>{activeKeys.length} active key{activeKeys.length === 1 ? '' : 's'} for this workspace.</p>
              </div>
              <button type="button" className="btn-primary text-xs" onClick={createKey} disabled={creatingKey}>
                {creatingKey ? 'Creating...' : 'Create Direct WhatsApp Key'}
              </button>
            </div>
            {newKey && (
              <div className="mt-3 rounded-lg p-3" style={{ background: 'rgba(34,201,122,.08)', border: '1px solid rgba(34,201,122,.25)' }}>
                <p className="text-xs font-bold mb-2" style={{ color: 'var(--success)' }}>Save this key now. It will not be shown again.</p>
                <MiniCopy label="New API key" value={newKey.key} />
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Inbound Reply Forwarding" description="When someone replies to the connected WhatsApp account, the platform saves it in the inbox and can also POST the reply to your own endpoint.">
        <div className="space-y-3">
          <label className="flex items-center gap-3 text-sm font-bold" style={{ color: 'var(--text)' }}>
            <input type="checkbox" checked={replyWebhookEnabled} onChange={(e) => setReplyWebhookEnabled(e.target.checked)} />
            Forward inbound Direct WhatsApp replies to my endpoint
          </label>
          <Field label="Reply webhook URL">
            <input className="input font-mono text-xs" value={replyWebhookUrl} onChange={(e) => setReplyWebhookUrl(e.target.value)} placeholder="https://yourdomain.com/whatsapp/replies" />
          </Field>
          <MiniCopy label="Example reply payload" value={replySample} />
        </div>
      </SectionCard>

      <div className="flex flex-col sm:flex-row gap-2">
        <button type="button" className="btn-primary text-sm" onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save Direct WhatsApp Settings'}
        </button>
        <button type="button" className="btn-secondary text-sm" onClick={load}>Reload</button>
      </div>
    </div>
  );
}

// ── EMAIL MARKETING TAB ──────────────────────────────────────────────────────
export function EmailMarketingTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domain, setDomain] = useState('');
  const [busyDomainId, setBusyDomainId] = useState(null);
  const [form, setForm] = useState({
    emailProvider: 'RESEND',
    emailApiKey: '',
    emailDefaultFromName: '',
    emailDefaultFromEmail: '',
    emailReplyTo: '',
    emailWebhookSecret: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await settingsApi.getEmailSettings();
      setSettings(data);
      setForm({
        emailProvider: data.provider || 'RESEND',
        emailApiKey: '',
        emailDefaultFromName: data.defaultFromName || '',
        emailDefaultFromEmail: data.defaultFromEmail || '',
        emailReplyTo: data.replyTo || '',
        emailWebhookSecret: '',
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load email settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.emailApiKey.trim()) delete payload.emailApiKey;
      if (!payload.emailWebhookSecret.trim()) delete payload.emailWebhookSecret;
      const { data } = await settingsApi.updateEmailSettings(payload);
      setSettings(data);
      setForm((prev) => ({ ...prev, emailApiKey: '', emailWebhookSecret: '' }));
      toast.success('Email settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save email settings');
    } finally {
      setSaving(false);
    }
  };

  const addDomain = async () => {
    if (!domain.trim()) return toast.error('Enter a sending domain');
    setBusyDomainId('new');
    try {
      await settingsApi.addEmailDomain({ domain });
      setDomain('');
      toast.success('Domain added. Add the DNS records shown below.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add domain');
    } finally {
      setBusyDomainId(null);
    }
  };

  const verify = async (id) => {
    setBusyDomainId(id);
    try {
      await settingsApi.verifyEmailDomain(id);
      toast.success('Domain status refreshed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not verify domain');
    } finally {
      setBusyDomainId(null);
    }
  };

  const remove = async (id) => {
    if (!confirm('Remove this email domain from the workspace?')) return;
    setBusyDomainId(id);
    try {
      await settingsApi.deleteEmailDomain(id);
      toast.success('Email domain removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove domain');
    } finally {
      setBusyDomainId(null);
    }
  };

  const recordsText = (records) => {
    if (!records) return 'No DNS records returned yet. Refresh after the provider creates the domain.';
    if (Array.isArray(records)) return records.map((record) => `${record.type || record.record || ''} ${record.name || record.host || ''} ${record.value || record.content || ''}`).join('\n');
    return JSON.stringify(records, null, 2);
  };

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: 'var(--textMuted)' }}>Loading email marketing...</p>;

  return (
    <div className="space-y-5">
      <SectionCard
        title="Email Sending Setup"
        description="Use a verified business domain for inbox emails, automation emails, customer notifications, and bulk email campaigns."
      >
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Provider">
            <select className="input" value={form.emailProvider} onChange={(e) => set('emailProvider', e.target.value)}>
              <option value="RESEND">Resend</option>
              <option value="SMTP">SMTP fallback</option>
            </select>
          </Field>
          <Field label="Provider API key">
            <input className="input" type="password" value={form.emailApiKey} onChange={(e) => set('emailApiKey', e.target.value)} placeholder={settings?.apiKeyStatus === 'server' ? 'Server key is configured' : settings?.apiKey || 'Paste API key'} />
          </Field>
          <Field label="Default from name">
            <input className="input" value={form.emailDefaultFromName} onChange={(e) => set('emailDefaultFromName', e.target.value)} placeholder="Maya Events" />
          </Field>
          <Field label="Default from email">
            <input className="input" value={form.emailDefaultFromEmail} onChange={(e) => set('emailDefaultFromEmail', e.target.value)} placeholder="hello@yourdomain.com" />
          </Field>
          <Field label="Reply-to email">
            <input className="input" value={form.emailReplyTo} onChange={(e) => set('emailReplyTo', e.target.value)} placeholder="support@yourdomain.com" />
          </Field>
          <Field label="Webhook secret">
            <input className="input" type="password" value={form.emailWebhookSecret} onChange={(e) => set('emailWebhookSecret', e.target.value)} placeholder={settings?.webhookSecret || 'Private shared secret'} />
          </Field>
        </div>
        <button type="button" className="btn-primary text-sm" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save Email Settings'}</button>
      </SectionCard>

      <SectionCard
        title="Sending Domains"
        description="Add the customer's domain, copy the provider DNS records into their DNS host, then refresh until it is verified."
      >
        <div className="flex flex-col sm:flex-row gap-2">
          <input className="input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="yourbusiness.com" />
          <button type="button" className="btn-primary text-sm flex-shrink-0" disabled={busyDomainId === 'new'} onClick={addDomain}>
            {busyDomainId === 'new' ? 'Adding...' : 'Add Domain'}
          </button>
        </div>
        <div className="grid gap-3">
          {(settings?.domains || []).length === 0 && (
            <div className="rounded-xl p-4 text-sm" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--textMuted)' }}>
              No email domain added yet. Add a domain before sending campaigns from a branded address.
            </div>
          )}
          {(settings?.domains || []).map((item) => (
            <div key={item.id} className="rounded-xl p-4 space-y-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <p className="font-bold" style={{ color: 'var(--text)' }}>{item.domain}</p>
                  <StatusPill value={item.status} />
                </div>
                <div className="flex gap-2">
                  <button type="button" className="btn-secondary text-xs" disabled={busyDomainId === item.id} onClick={() => verify(item.id)}>
                    {busyDomainId === item.id ? 'Checking...' : 'Refresh'}
                  </button>
                  <button type="button" className="btn-secondary text-xs" style={{ color: 'var(--danger)' }} disabled={busyDomainId === item.id} onClick={() => remove(item.id)}>Remove</button>
                </div>
              </div>
              <CopyRow label="DNS records" value={recordsText(item.dnsRecords)} />
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Tracking & Provider Webhook"
        description="Use this webhook for provider events such as delivered, opened, clicked, bounced, complained, failed, and unsubscribed."
      >
        <CopyRow label="Email provider webhook URL" value={settings?.webhookUrl || ''} />
        <CopyRow label="Open tracking pixel format" value={settings?.openTrackingUrl || ''} />
        <div className="rounded-xl p-3 text-xs leading-relaxed" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--textMuted)' }}>
          Add the webhook secret as <b>x-ineedlead-email-secret</b>, <b>x-webhook-secret</b>, or append <b>?secret=YOUR_SECRET</b> if the provider cannot send custom headers. Bounces and complaints automatically suppress that email address for this workspace only.
        </div>
      </SectionCard>
    </div>
  );
}

// ── NUMBERS TAB ───────────────────────────────────────────────────────────────
const numSchema = z.object({
  label: z.string().min(1, 'Required'),
  phoneNumber: z.string().min(7, 'Required'),
  channel: z.enum(['SMS','WHATSAPP']),
  accountSid: z.string().optional().or(z.literal('')),
  authToken: z.string().optional().or(z.literal('')),
  isDefault: z.boolean().optional(),
});

const twilioSetupSchema = z.object({
  defaultFromName: z.string().optional().or(z.literal('')),
  twilioAccountSid: z.string().regex(/^AC[0-9a-fA-F]{32}$/, 'Enter a valid Twilio Account SID'),
  twilioAuthToken: z.string().optional().or(z.literal('')),
  twilioMessagingServiceSid: z.string().optional().or(z.literal('')),
  a2pBrandStatus: z.enum(['NOT_STARTED', 'IN_PROGRESS', 'APPROVED', 'REJECTED']),
  a2pCampaignStatus: z.enum(['NOT_STARTED', 'IN_PROGRESS', 'APPROVED', 'REJECTED']),
});

function TwilioCredentialsCard({ onSaved }) {
  const [loading, setLoading] = useState(true);
  const [tokenStatus, setTokenStatus] = useState('missing');
  const [authTokenInput, setAuthTokenInput] = useState('');
  const [verified, setVerified] = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(twilioSetupSchema),
    defaultValues: {
      defaultFromName: '',
      twilioAccountSid: '',
      twilioAuthToken: '',
      twilioMessagingServiceSid: '',
      a2pBrandStatus: 'NOT_STARTED',
      a2pCampaignStatus: 'NOT_STARTED',
    },
  });

  useEffect(() => {
    settingsApi.getWorkspace()
      .then(({ data }) => {
        setTokenStatus(data.twilioAuthTokenStatus || (data.twilioAuthToken ? 'saved' : 'missing'));
        setAuthTokenInput('');
        reset({
          defaultFromName: data.defaultFromName || '',
          twilioAccountSid: data.twilioAccountSid || '',
          twilioAuthToken: '',
          twilioMessagingServiceSid: data.twilioMessagingServiceSid || '',
          a2pBrandStatus: data.a2pBrandStatus || 'NOT_STARTED',
          a2pCampaignStatus: data.a2pCampaignStatus || 'NOT_STARTED',
        });
      })
      .catch(() => toast.error('Could not load Twilio setup'))
      .finally(() => setLoading(false));
  }, [reset]);

  const save = async (values) => {
    const nextAuthToken = authTokenInput.trim();
    if (tokenStatus === 'needs_reentry' && !nextAuthToken) {
      toast.error('Paste the real Twilio Auth Token before saving');
      return;
    }

    try {
      const { data } = await settingsApi.saveTwilioCredentials({
        ...values,
        ...(nextAuthToken ? { twilioAuthToken: nextAuthToken } : {}),
      });
      const workspace = data.workspace || data;
      setAuthTokenInput('');
      reset({
        defaultFromName: workspace.defaultFromName || '',
        twilioAccountSid: workspace.twilioAccountSid || '',
        twilioAuthToken: '',
        twilioMessagingServiceSid: workspace.twilioMessagingServiceSid || '',
        a2pBrandStatus: workspace.a2pBrandStatus || 'NOT_STARTED',
        a2pCampaignStatus: workspace.a2pCampaignStatus || 'NOT_STARTED',
      });
      setTokenStatus('saved');
      setVerified(data.verified || null);
      toast.success('Twilio verified and saved');
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save Twilio setup');
    }
  };

  if (loading) return <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Loading Twilio setup...</p>;

  return (
    <SectionCard
      title="Twilio Account Credentials"
      description="Connect the customer's Twilio main account or subaccount here, then refresh Twilio below to see balance and available numbers."
    >
      <form onSubmit={handleSubmit(save)} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Sender name" error={errors.defaultFromName?.message}><input {...register('defaultFromName')} className="input" /></Field>
          <Field label="Twilio Account SID" error={errors.twilioAccountSid?.message}><input {...register('twilioAccountSid')} className="input font-mono text-xs" /></Field>
          <Field label="Twilio Auth Token" error={errors.twilioAuthToken?.message}>
            <input
              value={authTokenInput}
              onChange={e => setAuthTokenInput(e.target.value)}
              type="password"
              className="input font-mono text-xs"
              placeholder={tokenStatus === 'needs_reentry' ? 'Re-enter real Twilio Auth Token' : tokenStatus === 'saved' ? 'Saved. Leave blank to keep current token' : 'Paste Twilio Auth Token'}
            />
            {tokenStatus === 'needs_reentry' && (
              <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>Saved token cannot be decrypted. Paste the real Twilio Auth Token again and save.</p>
            )}
          </Field>
          <Field label="Messaging Service SID" error={errors.twilioMessagingServiceSid?.message}><input {...register('twilioMessagingServiceSid')} className="input font-mono text-xs" /></Field>
          <Field label="A2P brand status"><select {...register('a2pBrandStatus')} className="input"><option value="NOT_STARTED">Not started</option><option value="IN_PROGRESS">In progress</option><option value="APPROVED">Approved</option><option value="REJECTED">Rejected</option></select></Field>
          <Field label="A2P campaign status"><select {...register('a2pCampaignStatus')} className="input"><option value="NOT_STARTED">Not started</option><option value="IN_PROGRESS">In progress</option><option value="APPROVED">Approved</option><option value="REJECTED">Rejected</option></select></Field>
        </div>
        <button type="submit" disabled={isSubmitting} className="btn-primary text-sm">
          {isSubmitting ? 'Verifying...' : 'Verify & Save Twilio Setup'}
        </button>
        {verified?.balance && (
          <div className="rounded-lg px-3 py-2 text-xs" style={{ background: 'rgba(34,201,122,.10)', color: 'var(--success)', border: '1px solid rgba(34,201,122,.22)' }}>
            Verified {verified.accountStatus || 'active'} Twilio account. Current balance: {verified.balance.currency} {Number(verified.balance.balance || 0).toFixed(2)}
            {verified.messagingService ? ` · Messaging Service ${verified.messagingService.friendlyName || verified.messagingService.sid} verified.` : ''}
          </div>
        )}
      </form>
    </SectionCard>
  );
}

function NumberForm({ number, onSuccess, onClose }) {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(numSchema),
    defaultValues: number || { channel: 'SMS', isDefault: false },
  });
  const onSubmit = async data => {
    try {
      number?.id ? await settingsApi.updateNumber(number.id, data) : await settingsApi.createNumber(data);
      toast.success(number?.id ? 'Updated' : 'Added');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <Field label="Label *" error={errors.label?.message}>
        <input {...register('label')} className="input" placeholder="Main SMS, Support WhatsApp" />
      </Field>
      <Field label="Channel *">
        <select {...register('channel')} className="input">
          <option value="SMS">SMS</option>
          <option value="WHATSAPP">WhatsApp</option>
        </select>
      </Field>
      <Field label="Phone Number *" error={errors.phoneNumber?.message}>
        <input {...register('phoneNumber')} className="input" placeholder="+1234567890 or whatsapp:+1234567890" />
      </Field>
      <details className="rounded-lg" style={{ border: '1px solid var(--border)' }}>
        <summary className="px-3 py-2 text-xs cursor-pointer" style={{ color: 'var(--textMuted)' }}>
          Override Twilio credentials (optional)
        </summary>
        <div className="px-3 pb-3 space-y-2">
          <Field label="Account SID"><input {...register('accountSid')} className="input text-xs" placeholder="leave blank to use .env" /></Field>
          <Field label="Auth Token"><input {...register('authToken')} type="password" className="input text-xs" placeholder="leave blank to use .env" /></Field>
        </div>
      </details>
      <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--textMuted)' }}>
        <input {...register('isDefault')} type="checkbox" style={{ accentColor: 'var(--accent)' }} />
        Set as default for this channel
      </label>
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button type="submit" disabled={isSubmitting} className="btn-primary flex-1">{isSubmitting ? 'Saving…' : number?.id ? 'Update' : 'Add Number'}</button>
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

function TwilioKeywordHistoryImport() {
  const [form, setForm] = useState({
    keyword: 'DANCE',
    matchMode: 'exact',
    toNumber: '',
    fromDate: '',
    toDate: '',
    limit: 5000,
    tags: 'dance,event-subscriber',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const payload = (dryRun) => ({
    keyword: form.keyword.trim() || 'DANCE',
    matchMode: form.matchMode,
    toNumber: form.toNumber.trim(),
    fromDate: form.fromDate,
    toDate: form.toDate,
    limit: Number(form.limit) || 5000,
    tags: form.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
    dryRun,
  });

  const runImport = async (dryRun) => {
    if (!form.keyword.trim()) {
      toast.error('Enter the keyword to search for');
      return;
    }
    if (!dryRun && !confirm('Import matching contacts and save matching inbound messages into this workspace?')) return;

    setLoading(true);
    try {
      const { data } = await settingsApi.importTwilioKeywordHistory(payload(dryRun));
      setResult(data);
      toast.success(dryRun ? `Preview found ${data.uniqueContacts} contact${data.uniqueContacts === 1 ? '' : 's'}` : `Imported ${data.created} new and updated ${data.updated}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Twilio history import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SectionCard
      title="Import Old Keyword Texts"
      description="Find people who already texted a keyword to this workspace's Twilio number, then import them as contacts with message history."
    >
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <Field label="Keyword">
          <input value={form.keyword} onChange={(e) => update('keyword', e.target.value)} className="input" placeholder="DANCE" />
        </Field>
        <Field label="Match type">
          <select value={form.matchMode} onChange={(e) => update('matchMode', e.target.value)} className="input">
            <option value="exact">Exact keyword only</option>
            <option value="contains">Message contains keyword</option>
          </select>
        </Field>
        <Field label="Twilio number filter">
          <input value={form.toNumber} onChange={(e) => update('toNumber', e.target.value)} className="input font-mono text-xs" placeholder="+15551234567 optional" />
        </Field>
        <Field label="From date">
          <input type="date" value={form.fromDate} onChange={(e) => update('fromDate', e.target.value)} className="input" />
        </Field>
        <Field label="To date">
          <input type="date" value={form.toDate} onChange={(e) => update('toDate', e.target.value)} className="input" />
        </Field>
        <Field label="Messages to scan">
          <input type="number" min="1" max="20000" value={form.limit} onChange={(e) => update('limit', e.target.value)} className="input" />
        </Field>
        <div className="sm:col-span-2 xl:col-span-3">
          <Field label="Tags to add">
            <input value={form.tags} onChange={(e) => update('tags', e.target.value)} className="input" placeholder="dance,event-subscriber" />
          </Field>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button type="button" onClick={() => runImport(true)} disabled={loading} className="btn-secondary text-sm">
          {loading ? 'Checking...' : 'Preview Matches'}
        </button>
        <button type="button" onClick={() => runImport(false)} disabled={loading} className="btn-primary text-sm">
          {loading ? 'Working...' : 'Import Contacts'}
        </button>
      </div>

      {result && (
        <div className="rounded-xl p-3 space-y-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Scanned', result.scanned],
              ['Inbound', result.inboundScanned],
              ['Keyword matches', result.matched],
              ['Unique contacts', result.uniqueContacts],
              ['New contacts', result.created],
              ['Updated', result.updated],
              ['Messages saved', result.messagesSaved],
              ['Failed', result.failed],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg p-3" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <p className="text-[10px] uppercase font-bold tracking-wider" style={{ color: 'var(--textDim)' }}>{label}</p>
                <p className="text-xl font-bold mt-1" style={{ color: 'var(--text)' }}>{value ?? 0}</p>
              </div>
            ))}
          </div>
          {result.sample?.length > 0 && (
            <div>
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--textMuted)' }}>Sample matches</p>
              <div className="space-y-2">
                {result.sample.map((row) => (
                  <div key={row.twilioMessageSid || row.phone} className="rounded-lg p-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                    <code className="text-xs font-mono" style={{ color: 'var(--accent)' }}>{row.phone}</code>
                    <span className="text-xs flex-1 truncate" style={{ color: 'var(--textMuted)' }}>{row.body}</span>
                    {row.matchedAt && <span className="text-[10px]" style={{ color: 'var(--textDim)' }}>{new Date(row.matchedAt).toLocaleString()}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.failures?.length > 0 && (
            <div className="rounded-lg p-3 text-xs" style={{ background: 'rgba(239,17,17,.10)', color: 'var(--danger)', border: '1px solid rgba(239,17,17,.25)' }}>
              {result.failures.length} item{result.failures.length === 1 ? '' : 's'} need review. First issue: {result.failures[0].message}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

export function VoiceCallsSettings() {
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [ivrAutomations, setIvrAutomations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [configuringIvr, setConfiguringIvr] = useState(false);
  const [lastIvrUrl, setLastIvrUrl] = useState('');
  const [creatingAndroidPush, setCreatingAndroidPush] = useState(false);

  const isIvrAutomation = (automation) => {
    const nodes = automation?.flow?.nodes;
    return Array.isArray(nodes) && nodes.some(node => node?.data?.nodeType === 'ivr_call');
  };

  const load = () => Promise.all([
    settingsApi.getWorkspace(),
    automationsApi.list().catch(() => ({ data: [] })),
  ])
    .then(([{ data }, automations]) => {
      const publishedIvr = (automations.data || [])
        .filter(automation => automation.status === 'ACTIVE' && isIvrAutomation(automation));
      setIvrAutomations(publishedIvr);
      setLastIvrUrl('');
      setWorkspace({
      callsEnabled: Boolean(data.callsEnabled),
      voiceApiKeySid: data.voiceApiKeySid || '',
      voiceApiKeySecret: data.voiceApiKeySecret || '',
      voiceApiKeySecretStatus: data.voiceApiKeySecretStatus || 'missing',
      voiceTwimlAppSid: data.voiceTwimlAppSid || '',
      voiceAndroidPushCredentialSid: data.voiceAndroidPushCredentialSid || '',
      voiceIosPushCredentialSid: data.voiceIosPushCredentialSid || '',
      voiceForwardMode: data.voiceForwardMode || 'WEBAPP',
      voiceIvrAutomationId: data.voiceIvrAutomationId || publishedIvr[0]?.id || '',
      voiceForwardNumber: data.voiceForwardNumber || '',
      voiceRingSeconds: data.voiceRingSeconds || 25,
      voiceFallbackAction: data.voiceFallbackAction || 'VOICEMAIL',
      missedCallSmsEnabled: data.missedCallSmsEnabled !== false,
      missedCallSmsTemplate: data.missedCallSmsTemplate || '',
      voicemailPromptText: data.voicemailPromptText || '',
      voicemailTranscribe: data.voicemailTranscribe !== false,
    });
    })
    .catch(() => {});

  useEffect(() => { load(); }, []);

  if (!workspace?.callsEnabled) return null;

  const set = (key, value) => setWorkspace(prev => ({ ...prev, [key]: value }));
  const routeHelp = {
    WEBAPP: 'Calls ring the browser and mobile softphone. Best when someone keeps the app open during business hours.',
    IVR: 'Callers hear the selected IVR automation first. Use this for press-1 menus, lead qualification, WhatsApp follow-up, and smart call routing.',
    PHONE: 'Calls go straight to the phone number below. Best for simple forwarding.',
    BOTH: 'Calls ring the app and backup phone together, so a real phone can still answer when the app is closed.',
  }[workspace.voiceForwardMode] || '';
  const selectedIvr = ivrAutomations.find(automation => automation.id === workspace.voiceIvrAutomationId);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...workspace };
      if (payload.voiceApiKeySecret?.startsWith('••••')) delete payload.voiceApiKeySecret;
      delete payload.callsEnabled;
      const { data } = await settingsApi.saveVoiceSettings(payload);
      setWorkspace(prev => ({ ...prev, ...data }));
      if (data.twimlAppWarning) {
        toast.error(`Call settings saved, but TwiML App auto-config needs attention: ${data.twimlAppWarning}`);
      } else {
        toast.success('Call settings saved and TwiML App configured');
      }
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save call settings');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const configureSelectedIvr = async () => {
    if (!workspace.voiceIvrAutomationId) {
      toast.error('Choose a published IVR automation first');
      return;
    }
    setConfiguringIvr(true);
    try {
      const saved = await save();
      if (!saved) return;
      const { data } = await settingsApi.configureSelectedIvrNumber({});
      setLastIvrUrl(data.voiceUrl || '');
      toast.success(`Twilio voice webhook now uses ${data.automation?.name || 'the selected IVR'}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not configure IVR on Twilio');
    } finally {
      setConfiguringIvr(false);
    }
  };

  const createAndroidPushCredential = async () => {
    setCreatingAndroidPush(true);
    try {
      const { data } = await settingsApi.createAndroidPushCredential({});
      setWorkspace(prev => ({
        ...prev,
        voiceAndroidPushCredentialSid: data.voiceAndroidPushCredentialSid || prev.voiceAndroidPushCredentialSid,
      }));
      toast.success('Mobile call notifications are ready for Android');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not enable mobile call notifications');
    } finally {
      setCreatingAndroidPush(false);
    }
  };

  return (
      <SectionCard
        title="Voice Calls"
      description="Configure inbound call routing, IVR menus, browser/mobile softphone, forwarding, voicemail, missed-call SMS, and Twilio Voice webhooks for this workspace."
    >
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Voice API Key SID">
          <input className="input font-mono text-xs" value={workspace.voiceApiKeySid} onChange={e => set('voiceApiKeySid', e.target.value)} placeholder="SK..." />
        </Field>
        <Field label="Voice API Key Secret">
          <input className="input font-mono text-xs" value={workspace.voiceApiKeySecret} onChange={e => set('voiceApiKeySecret', e.target.value)} placeholder={workspace.voiceApiKeySecretStatus === 'saved' ? 'Saved. Leave blank/unchanged' : 'Paste Twilio API key secret'} />
        </Field>
        <Field label="TwiML App SID">
          <input className="input font-mono text-xs" value={workspace.voiceTwimlAppSid} onChange={e => set('voiceTwimlAppSid', e.target.value)} placeholder="AP..." />
        </Field>
        <Field label="Inbound call route">
          <select className="input" value={workspace.voiceForwardMode} onChange={e => set('voiceForwardMode', e.target.value)}>
            <option value="WEBAPP">Browser softphone</option>
            <option value="IVR">IVR phone menu</option>
            <option value="PHONE">Forward to phone number</option>
            <option value="BOTH">Browser softphone + backup phone</option>
          </select>
          <p className="text-[11px] mt-1" style={{ color: 'var(--textDim)' }}>
            {routeHelp}
          </p>
        </Field>
        <Field label="Forward / backup phone number">
          <input className="input" value={workspace.voiceForwardNumber} onChange={e => set('voiceForwardNumber', e.target.value)} placeholder="+15551234567" disabled={!['PHONE', 'BOTH'].includes(workspace.voiceForwardMode)} />
        </Field>
        <Field label="Ring seconds">
          <input type="number" min="5" max="60" className="input" value={workspace.voiceRingSeconds} onChange={e => set('voiceRingSeconds', Number(e.target.value))} />
        </Field>
      </div>
      {workspace.voiceForwardMode === 'IVR' && (
        <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>Selected IVR menu</p>
              <p className="text-xs mt-1 max-w-2xl" style={{ color: 'var(--textMuted)' }}>
                Publish an automation that starts with <b>Incoming Call</b>, then choose it here. This workspace will answer calls with that menu before ringing anyone.
              </p>
            </div>
            <button type="button" className="btn-secondary text-xs" onClick={() => navigate('/automations')}>
              Build IVR Flow
            </button>
          </div>
          {ivrAutomations.length ? (
            <Field label="IVR automation">
              <select className="input" value={workspace.voiceIvrAutomationId} onChange={e => set('voiceIvrAutomationId', e.target.value)}>
                {ivrAutomations.map(automation => (
                  <option key={automation.id} value={automation.id}>{automation.name}</option>
                ))}
              </select>
              <p className="text-[11px] mt-1" style={{ color: 'var(--textDim)' }}>
                {selectedIvr ? `${selectedIvr.name} is live and will answer inbound calls for this workspace.` : 'Choose the live IVR menu for this workspace.'}
              </p>
            </Field>
          ) : (
            <div className="rounded-lg p-3 text-xs leading-relaxed" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--textMuted)' }}>
              No published IVR automation yet. Go to Automations, start from the <b>Driving School IVR</b> template or add an <b>Incoming Call</b> trigger, then publish it and return here.
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <button
              type="button"
              onClick={configureSelectedIvr}
              disabled={configuringIvr || !ivrAutomations.length}
              className="btn-primary text-sm justify-center"
            >
              {configuringIvr ? 'Configuring IVR...' : 'Auto-configure Twilio for IVR'}
            </button>
            <p className="text-[11px] leading-relaxed" style={{ color: 'var(--textDim)' }}>
              Saves this route and points the saved/default Twilio number to the exact selected IVR URL.
            </p>
          </div>
          {lastIvrUrl && <MiniCopy label="Exact IVR URL installed on Twilio" value={lastIvrUrl} />}
        </div>
      )}
      <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>Mobile app call notifications</p>
            <p className="text-xs mt-1 max-w-2xl" style={{ color: 'var(--textMuted)' }}>
              Enable ringing on the Android mobile app. On the phone, the user also needs to allow microphone and notification permissions when the app asks.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            {workspace.voiceAndroidPushCredentialSid && (
              <span className="text-[11px] font-bold px-3 py-2 rounded-full text-center" style={{ color: 'var(--success)', background: 'rgba(34,197,94,.12)' }}>
                Enabled
              </span>
            )}
            <button
              type="button"
              onClick={createAndroidPushCredential}
              disabled={creatingAndroidPush}
              className="btn-primary text-sm whitespace-nowrap"
            >
              {creatingAndroidPush ? 'Enabling...' : workspace.voiceAndroidPushCredentialSid ? 'Re-enable Notifications' : 'Enable Notifications'}
            </button>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Missed call SMS">
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--textMuted)' }}>
            <input type="checkbox" checked={workspace.missedCallSmsEnabled} onChange={e => set('missedCallSmsEnabled', e.target.checked)} />
            Send automatic SMS after missed calls
          </label>
        </Field>
        <Field label="Fallback action">
          <select className="input" value={workspace.voiceFallbackAction} onChange={e => set('voiceFallbackAction', e.target.value)}>
            <option value="VOICEMAIL">Record voicemail</option>
            <option value="HANGUP">Hang up</option>
          </select>
        </Field>
        <Field label="Missed call text">
          <textarea className="input min-h-[90px]" value={workspace.missedCallSmsTemplate} onChange={e => set('missedCallSmsTemplate', e.target.value)} />
        </Field>
        <Field label="Voicemail prompt">
          <textarea className="input min-h-[90px]" value={workspace.voicemailPromptText} onChange={e => set('voicemailPromptText', e.target.value)} />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--textMuted)' }}>
        <input type="checkbox" checked={workspace.voicemailTranscribe} onChange={e => set('voicemailTranscribe', e.target.checked)} />
        Ask Twilio to transcribe voicemail when available
      </label>
      <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm">
        {saving ? 'Saving...' : 'Save Call Settings'}
      </button>
    </SectionCard>
  );
}

function VoiceReadinessCard({ voice }) {
  if (!voice?.enabled) return null;
  const isIvrMode = voice.mode === 'IVR';
  const isSoftphoneMode = !['IVR', 'PHONE'].includes(voice.mode || 'WEBAPP');
  const rows = [
    ['Twilio account connected', voice.hasTwilioCredentials, 'Save the Account SID and Auth Token above.'],
    ...(isIvrMode ? [
      ['Published IVR menu selected', Boolean(voice.selectedIvrAutomationId), 'Choose a published IVR automation in Voice Calls settings.'],
    ] : []),
    ...(isSoftphoneMode ? [
      ['Voice API Key SID saved', voice.hasVoiceApiKeySid, 'Add the SK... API Key SID in Voice Calls settings.'],
      ['Voice API Key Secret saved', voice.hasVoiceApiKeySecret, 'Add the Twilio API Key Secret in Voice Calls settings.'],
      ['TwiML App SID saved', voice.hasTwimlAppSid, 'Add the AP... TwiML App SID in Voice Calls settings.'],
    ] : []),
    ['Twilio number voice webhook ready', voice.webhookReady, isIvrMode ? 'Click Auto-configure Twilio for IVR, or use Use This Number below.' : 'Click Use This Number/Reconfigure on a Twilio number to set the voice URL.'],
  ];

  return (
    <SectionCard
      title={voice.ready ? (isIvrMode ? 'IVR Calling Active' : 'Voice Calling Active') : (isIvrMode ? 'IVR Setup' : 'Voice Calling Setup')}
      description={voice.ready
        ? (isIvrMode ? 'Inbound calls now answer with the selected IVR menu.' : 'This workspace has the browser softphone credentials and Twilio number voice webhook configured.')
        : (isIvrMode ? 'Choose a published IVR menu and auto-configure the Twilio number. Softphone API keys are only needed if the IVR forwards to the app.' : 'Complete these items before calls can reliably ring in the browser and place outbound calls.')}
      tone={voice.ready ? 'soft' : 'default'}
    >
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-4">
        <div className="rounded-xl p-4" style={{
          background: voice.ready ? 'rgba(34,201,122,.12)' : 'rgba(245,166,35,.12)',
          border: `1px solid ${voice.ready ? 'rgba(34,201,122,.35)' : 'rgba(245,166,35,.30)'}`,
        }}>
          <p className="text-[10px] uppercase font-bold tracking-widest" style={{ color: voice.ready ? 'var(--success)' : 'var(--warning)' }}>
            {voice.ready ? 'Active' : 'Needs setup'}
          </p>
          <p className="text-2xl font-bold mt-2" style={{ color: 'var(--text)' }}>
            {voice.ready ? 'Calls Ready' : `${rows.filter(([, ok]) => ok).length}/${rows.length} Complete`}
          </p>
          <p className="text-xs mt-2" style={{ color: 'var(--textMuted)' }}>
            The softphone button will turn green when this browser also registers with Twilio.
          </p>
        </div>
        <div className="space-y-2">
          {rows.map(([label, ok, fix]) => (
            <div key={label} className="rounded-xl p-3 flex items-start gap-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <span className="mt-0.5 h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold" style={{
                background: ok ? 'rgba(34,201,122,.16)' : 'rgba(239,17,17,.12)',
                color: ok ? 'var(--success)' : 'var(--danger)',
              }}>
                {ok ? '✓' : '!'}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{label}</p>
                {!ok && <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>{fix}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        {voice.selectedUrl && <CopyRow label={isIvrMode ? 'Selected IVR Voice URL' : 'Selected Voice URL'} value={voice.selectedUrl} />}
        {voice.inboundUrl && <CopyRow label="Phone Number Inbound Voice URL" value={voice.inboundUrl} />}
        {voice.ivrUrl && <CopyRow label="General IVR Voice URL" value={voice.ivrUrl} />}
        {voice.appUrl && <CopyRow label="Per-Customer TwiML App Voice URL" value={voice.appUrl} />}
      </div>
      <p className="text-xs leading-relaxed" style={{ color: 'var(--textMuted)' }}>
        {isIvrMode
          ? 'For IVR-only phone menus, Twilio can point directly to the selected IVR Voice URL. Browser/mobile softphone credentials are only required when the IVR forwards calls into the app.'
          : 'Use the phone number URL on the Twilio number Voice webhook. Use the per-customer TwiML App URL in Twilio Voice > TwiML Apps > Voice Configuration > Request URL with HTTP POST.'}
      </p>
    </SectionCard>
  );
}

export function NumbersTab({ showVoice = true } = {}) {
  const [numbers, setNumbers] = useState([]);
  const [twilioStatus, setTwilioStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [configuringSid, setConfiguringSid] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editNum, setEditNum] = useState(null);
  const load = () => settingsApi.listNumbers().then(({ data }) => setNumbers(data)).catch(() => toast.error('Failed'));
  const loadTwilio = () => {
    setSyncing(true);
    settingsApi.getTwilioSetupStatus()
      .then(({ data }) => setTwilioStatus(data))
      .catch(() => toast.error('Could not load Twilio account details'))
      .finally(() => setSyncing(false));
  };
  useEffect(() => { load(); loadTwilio(); }, []);
  const del = async id => { if (!confirm('Remove?')) return; try { await settingsApi.deleteNumber(id); load(); } catch { toast.error('Failed'); } };
  const setDef = async id => { try { await settingsApi.setDefaultNumber(id); load(); toast.success('Default updated'); } catch { toast.error('Failed'); } };
  const configureRemote = async (remote) => {
    setConfiguringSid(remote.sid);
    try {
      const { data } = await settingsApi.configureTwilioNumber({
        sid: remote.sid,
        phoneNumber: remote.phoneNumber,
        friendlyName: remote.friendlyName,
        channel: 'SMS',
        makeDefault: true,
      });
      toast.success(`${remote.phoneNumber} configured and set as default`);
      if (data.messagingServiceWarning) {
        toast.error(`Number saved, but Messaging Service update needs attention: ${data.messagingServiceWarning}`);
      } else if (data.messagingService) {
        toast.success('Messaging Service webhooks configured too');
      }
      setTwilioStatus((prev) => ({
        ...prev,
        numbers: (prev?.numbers || []).map((n) => n.sid === remote.sid ? data.remote : n),
        webhookUrls: data.webhookUrls ? { ...(prev?.webhookUrls || {}), ...data.webhookUrls } : prev?.webhookUrls,
        messagingService: data.messagingService || prev?.messagingService,
        voice: data.voice || prev?.voice,
      }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not configure Twilio number');
    } finally {
      setConfiguringSid('');
    }
  };

  const configuredPhones = new Set(numbers.filter(n => n.channel === 'SMS').map(n => n.phoneNumber));

  return (
    <div className="space-y-4">
      <TwilioCredentialsCard onSaved={(result) => {
        setTwilioStatus((prev) => ({
          ...(prev || {}),
          hasCredentials: true,
          accountSid: result?.verified?.accountSid || prev?.accountSid || '',
          balance: result?.verified?.balance || prev?.balance || null,
          messagingService: result?.verified?.messagingService || prev?.messagingService || null,
          error: null,
          messagingServiceError: null,
        }));
        loadTwilio();
      }} />

      {showVoice && <VoiceCallsSettings />}
      {showVoice && <VoiceReadinessCard voice={twilioStatus?.voice} />}

      <div className="rounded-xl p-4 space-y-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Connected Twilio Account</p>
            <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>
              Check balance, fetch Twilio phone numbers, and auto-connect the chosen number for SMS, Twilio WhatsApp, voice, or the selected IVR route.
            </p>
          </div>
          <button type="button" onClick={loadTwilio} disabled={syncing} className="btn-secondary text-sm flex-shrink-0">
            {syncing ? 'Checking...' : 'Refresh Twilio'}
          </button>
        </div>

        {!twilioStatus?.hasCredentials ? (
          <div className="rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--accentSoft)', color: 'var(--accent)', border: '1px solid var(--border)' }}>
            Save the Twilio Account SID and Auth Token above, then refresh Twilio to fetch numbers and balance.
          </div>
        ) : twilioStatus?.error ? (
          <div className="rounded-lg px-3 py-2 text-xs" style={{ background: 'rgba(239,17,17,.10)', color: 'var(--danger)', border: '1px solid rgba(239,17,17,.25)' }}>
            Twilio error: {twilioStatus.error}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-3">
            <div className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <p className="text-[10px] uppercase font-bold tracking-wider" style={{ color: 'var(--textDim)' }}>Balance</p>
              <p className="text-2xl font-bold mt-1" style={{ color: 'var(--text)' }}>
                {twilioStatus?.balance ? `${twilioStatus.balance.currency} ${Number(twilioStatus.balance.balance || 0).toFixed(2)}` : '—'}
              </p>
              <p className="text-[10px] mt-1 font-mono break-anywhere" style={{ color: 'var(--textDim)' }}>{twilioStatus?.accountSid}</p>
            </div>
            <div className="rounded-lg p-3 md:col-span-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <p className="text-[10px] uppercase font-bold tracking-wider mb-2" style={{ color: 'var(--textDim)' }}>Twilio webhook URLs</p>
              <p className="text-[11px] mb-2 leading-relaxed" style={{ color: 'var(--textMuted)' }}>
                SMS and Twilio WhatsApp replies use the same inbound URL. Direct WhatsApp API replies are configured separately under Direct WhatsApp API.
              </p>
              <div className="space-y-1">
                <code className="block text-xs break-anywhere" style={{ color: 'var(--accent)' }}>{twilioStatus?.webhookUrls?.inbound}</code>
                <code className="block text-xs break-anywhere" style={{ color: 'var(--accent)' }}>{twilioStatus?.webhookUrls?.status}</code>
                {twilioStatus?.webhookUrls?.selectedVoice && (
                  <code className="block text-xs break-anywhere" style={{ color: 'var(--accent)' }}>{twilioStatus.webhookUrls.selectedVoice}</code>
                )}
              </div>
              {twilioStatus?.messagingService && (
                <p className="text-[10px] mt-2" style={{ color: 'var(--success)' }}>
                  Messaging Service connected: {twilioStatus.messagingService.friendlyName || twilioStatus.messagingService.sid}
                </p>
              )}
              {twilioStatus?.messagingServiceError && (
                <p className="text-[10px] mt-2" style={{ color: 'var(--danger)' }}>
                  Messaging Service warning: {twilioStatus.messagingServiceError}
                </p>
              )}
            </div>
          </div>
        )}

        {twilioStatus?.numbers?.length > 0 && (
          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <p className="text-xs font-semibold" style={{ color: 'var(--textMuted)' }}>Numbers found in Twilio</p>
              <span className="text-[10px]" style={{ color: 'var(--textDim)' }}>{twilioStatus.numbers.length} number{twilioStatus.numbers.length === 1 ? '' : 's'}</span>
            </div>
            {twilioStatus.numbers.map((remote) => {
              const configured = configuredPhones.has(remote.phoneNumber);
              const webhooksReady = remote.smsUrl === twilioStatus?.webhookUrls?.inbound && remote.statusCallback === twilioStatus?.webhookUrls?.status;
              const expectedVoiceUrl = twilioStatus?.webhookUrls?.selectedVoice || twilioStatus?.webhookUrls?.voiceIncoming;
              const voiceReady = expectedVoiceUrl && remote.voiceUrl === expectedVoiceUrl;
              const canConfigure = Boolean(remote.capabilities?.sms || (twilioStatus?.voice?.enabled && remote.capabilities?.voice));
              return (
                <div key={remote.sid} className="rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{remote.friendlyName || remote.phoneNumber}</p>
                      {remote.capabilities?.sms && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--accentSoft)', color: 'var(--accent)' }}>SMS</span>}
                      {remote.capabilities?.voice && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--accentSoft)', color: 'var(--accent)' }}>VOICE</span>}
                      {configured && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,201,122,.12)', color: 'var(--success)' }}>SAVED</span>}
                      {webhooksReady && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,201,122,.12)', color: 'var(--success)' }}>WEBHOOK READY</span>}
                      {voiceReady && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,201,122,.12)', color: 'var(--success)' }}>VOICE READY</span>}
                    </div>
                    <code className="text-xs font-mono block mt-1 break-anywhere" style={{ color: 'var(--textMuted)' }}>{remote.phoneNumber}</code>
                    <p className="text-[10px] mt-1 break-anywhere" style={{ color: 'var(--textDim)' }}>SID {remote.sid}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => configureRemote(remote)}
                    disabled={configuringSid === remote.sid || !canConfigure}
                    className="btn-primary text-sm flex-shrink-0"
                  >
                    {configuringSid === remote.sid ? 'Configuring...' : webhooksReady && configured ? 'Reconfigure' : 'Use This Number'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <TwilioKeywordHistoryImport />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-xs" style={{ color: 'var(--textMuted)' }}>Saved sender numbers. The default SMS number is used for campaigns and replies.</p>
        <button onClick={() => { setEditNum(null); setShowForm(true); }} className="btn-primary text-sm">Add Manually</button>
      </div>
      {numbers.length === 0
        ? <div className="rounded-xl p-10 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <p className="text-3xl mb-2">📱</p>
            <p className="text-sm" style={{ color: 'var(--textMuted)' }}>No sender number saved yet. Use a Twilio number above or add one manually.</p>
          </div>
        : <div className="space-y-2">
            {numbers.map(n => (
              <div key={n.id} className="rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                style={{ background: 'var(--card)', border: '1px solid var(--border)', opacity: n.isActive ? 1 : 0.5 }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{n.label}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                      style={{ background: n.channel === 'WHATSAPP' ? 'rgba(37,211,102,.1)' : 'rgba(59,167,255,.1)', color: n.channel === 'WHATSAPP' ? 'var(--whatsapp)' : 'var(--info)' }}>
                      {n.channel}
                    </span>
                    {n.isDefault && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: 'var(--accentSoft)', color: 'var(--accent)' }}>DEFAULT</span>}
                  </div>
                  <code className="text-xs font-mono" style={{ color: 'var(--textMuted)' }}>{n.phoneNumber}</code>
                </div>
                <div className="flex gap-3 text-xs flex-shrink-0 w-full sm:w-auto justify-end">
                  {!n.isDefault && <button onClick={() => setDef(n.id)} style={{ color: 'var(--accent)' }} className="hover:opacity-70">Default</button>}
                  <button onClick={() => { setEditNum(n); setShowForm(true); }} style={{ color: 'var(--textMuted)' }} className="hover:opacity-70">Edit</button>
                  <button onClick={() => del(n.id)} style={{ color: 'var(--danger)' }} className="hover:opacity-70">Remove</button>
                </div>
              </div>
            ))}
          </div>}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editNum ? 'Edit Number' : 'Add Number'}>
        <NumberForm number={editNum} onSuccess={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />
      </Modal>
    </div>
  );
}

// ── ENDPOINTS TAB ─────────────────────────────────────────────────────────────
const epSchema = z.object({
  label: z.string().min(1, 'Required'),
  baseUrl: z.string().url('Must be a valid URL'),
  description: z.string().optional(),
});

function EndpointForm({ endpoint, onSuccess, onClose }) {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(epSchema), defaultValues: endpoint || {},
  });
  const onSubmit = async data => {
    try {
      endpoint?.id ? await settingsApi.updateEndpoint(endpoint.id, data) : await settingsApi.createEndpoint(data);
      toast.success(endpoint?.id ? 'Updated' : 'Added'); onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <Field label="Label *" error={errors.label?.message}><input {...register('label')} className="input" placeholder="Production, Staging" /></Field>
      <Field label="Base URL *" error={errors.baseUrl?.message}><input {...register('baseUrl')} className="input" placeholder="https://yourdomain.com" /></Field>
      <Field label="Description"><input {...register('description')} className="input" placeholder="Optional note" /></Field>
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button type="submit" disabled={isSubmitting} className="btn-primary flex-1">{isSubmitting ? 'Saving…' : endpoint?.id ? 'Update' : 'Add Endpoint'}</button>
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

export function EndpointsTab() {
  const [endpoints, setEndpoints] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editEp, setEditEp] = useState(null);
  const load = () => settingsApi.listEndpoints().then(({ data }) => setEndpoints(data)).catch(() => toast.error('Failed'));
  useEffect(() => { load(); }, []);
  const del = async id => { if (!confirm('Delete?')) return; try { await settingsApi.deleteEndpoint(id); load(); } catch { toast.error('Failed'); } };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-xs" style={{ color: 'var(--textMuted)' }}>Choose which live domain is used when the app generates Twilio webhook links.</p>
        <button onClick={() => { setEditEp(null); setShowForm(true); }} className="btn-primary text-sm">Add Domain URL</button>
      </div>
      {endpoints.length === 0
        ? <div className="rounded-xl p-10 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <p className="text-3xl mb-2">🌐</p>
            <p className="text-sm" style={{ color: 'var(--textMuted)' }}>No custom webhook domain yet. The app will use the main platform domain.</p>
          </div>
        : <div className="space-y-2">
            {endpoints.map(e => (
              <div key={e.id} className="rounded-xl p-4 flex items-center gap-4"
                style={{ background: 'var(--card)', border: '1px solid var(--border)', opacity: e.isActive ? 1 : 0.5 }}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm mb-0.5" style={{ color: 'var(--text)' }}>{e.label}</p>
                  <code className="text-xs font-mono" style={{ color: 'var(--accent)' }}>{e.baseUrl}</code>
                  {e.description && <p className="text-xs mt-0.5" style={{ color: 'var(--textDim)' }}>{e.description}</p>}
                </div>
                <div className="flex gap-3 text-xs flex-shrink-0">
                  <button onClick={() => { setEditEp(e); setShowForm(true); }} style={{ color: 'var(--textMuted)' }} className="hover:opacity-70">Edit</button>
                  <button onClick={() => del(e.id)} style={{ color: 'var(--danger)' }} className="hover:opacity-70">Delete</button>
                </div>
              </div>
            ))}
          </div>}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editEp ? 'Edit Endpoint' : 'Add Endpoint'}>
        <EndpointForm endpoint={editEp} onSuccess={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />
      </Modal>
    </div>
  );
}

// ── USERS TAB ─────────────────────────────────────────────────────────────────
const createUserSchema = z.object({
  name: z.string().min(1), email: z.string().email(),
  password: z.string().min(8, 'Min 8 characters'),
  role: z.enum(['ADMIN','MANAGER','AGENT']),
});

function CreateUserForm({ onSuccess, onClose }) {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(createUserSchema), defaultValues: { role: 'AGENT' },
  });
  const onSubmit = async data => {
    try { await usersApi.create(data); toast.success('User created'); onSuccess(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <Field label="Name" error={errors.name?.message}><input {...register('name')} className="input" placeholder="Full name" /></Field>
      <Field label="Email" error={errors.email?.message}><input {...register('email')} type="email" className="input" /></Field>
      <Field label="Password" error={errors.password?.message}><input {...register('password')} type="password" className="input" /></Field>
      <Field label="Role">
        <select {...register('role')} className="input">
          <option value="AGENT">Agent</option><option value="MANAGER">Manager</option><option value="ADMIN">Admin</option>
        </select>
      </Field>
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button type="submit" disabled={isSubmitting} className="btn-primary flex-1">{isSubmitting ? 'Creating…' : 'Create User'}</button>
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

const resetPwSchema = z.object({
  newPassword: z.string().min(8, 'Min 8 characters'),
  confirmPassword: z.string().min(1, 'Required'),
}).refine(d => d.newPassword === d.confirmPassword, { message: "Passwords don't match", path: ['confirmPassword'] });

function ResetPasswordForm({ user, onSuccess, onClose }) {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(resetPwSchema) });
  const onSubmit = async ({ newPassword }) => {
    try { await usersApi.resetPassword(user.id, newPassword); toast.success(`Password reset for ${user.name}`); reset(); onSuccess(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Set a new password for <strong style={{ color: 'var(--text)' }}>{user?.name}</strong>.</p>
      <Field label="New Password" error={errors.newPassword?.message}><input {...register('newPassword')} type="password" className="input" placeholder="Min 8 characters" /></Field>
      <Field label="Confirm Password" error={errors.confirmPassword?.message}><input {...register('confirmPassword')} type="password" className="input" /></Field>
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button type="submit" disabled={isSubmitting} className="btn-primary flex-1">{isSubmitting ? 'Resetting…' : 'Reset Password'}</button>
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

export function UsersTab() {
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editRoleUser, setEditRoleUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [resetPwUser, setResetPwUser] = useState(null);

  const load = async () => { try { const { data } = await usersApi.list(); setUsers(data); } catch { toast.error('Failed to load users'); } };
  useEffect(() => { load(); }, []);

  const toggleActive = async u => {
    try { await usersApi.update(u.id, { isActive: !u.isActive }); toast.success(u.isActive ? 'Deactivated' : 'Activated'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const changeRole = async () => {
    if (!editRoleUser || !selectedRole) return;
    try { await settingsApi.updateUserRole(editRoleUser.id, selectedRole); toast.success(`Role changed to ${selectedRole}`); setEditRoleUser(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-xs" style={{ color: 'var(--textMuted)' }}>Invite staff, reset passwords, and control what each person can access.</p>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-sm">Add Team Member</button>
      </div>

      <div className="rounded-xl overflow-hidden responsive-scroll" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Name','Email','Role','Status','Last Seen','Actions'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--textDim)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const rs = ROLE_STYLE[u.role] || ROLE_STYLE.AGENT;
              return (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td className="px-4 py-3 font-semibold" style={{ color: 'var(--text)' }}>{u.name}</td>
                  <td className="px-4 py-3" style={{ color: 'var(--textMuted)' }}>{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: rs.bg, color: rs.text }}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: u.isActive ? 'rgba(34,201,122,.1)' : 'var(--card)', color: u.isActive ? 'var(--success)' : 'var(--textDim)', border: '1px solid var(--border)' }}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--textDim)' }}>
                    {u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 text-xs">
                      <button onClick={() => { setEditRoleUser(u); setSelectedRole(u.role); }} style={{ color: 'var(--accent)' }} className="hover:opacity-70">Role</button>
                      <button onClick={() => setResetPwUser(u)} style={{ color: 'var(--warning)' }} className="hover:opacity-70">Reset PW</button>
                      <button onClick={() => toggleActive(u)} style={{ color: 'var(--textMuted)' }} className="hover:opacity-70">{u.isActive ? 'Deactivate' : 'Activate'}</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal isOpen={!!editRoleUser} onClose={() => setEditRoleUser(null)} title={`Change role — ${editRoleUser?.name}`}>
        <div className="space-y-4">
          <div className="space-y-2">
            {['ADMIN','MANAGER','AGENT'].map(role => {
              const rs = ROLE_STYLE[role];
              return (
                <label key={role}
                  className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors"
                  style={{ border: `1px solid ${selectedRole === role ? 'var(--accent)' : 'var(--border)'}`, background: selectedRole === role ? 'var(--accentSoft)' : 'transparent' }}>
                  <input type="radio" name="role" value={role} checked={selectedRole === role} onChange={() => setSelectedRole(role)} style={{ accentColor: 'var(--accent)' }} />
                  <div>
                    <span className="text-sm font-semibold" style={{ color: rs.text }}>{role}</span>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--textDim)' }}>
                      {role === 'ADMIN' && 'Full access to all features'}
                      {role === 'MANAGER' && 'Manage contacts, import/export, assign agents'}
                      {role === 'AGENT' && 'View and send messages to assigned contacts'}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={changeRole} className="btn-primary flex-1">Save Role</button>
            <button onClick={() => setEditRoleUser(null)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!resetPwUser} onClose={() => setResetPwUser(null)} title="Reset Password">
        {resetPwUser && <ResetPasswordForm user={resetPwUser} onSuccess={() => setResetPwUser(null)} onClose={() => setResetPwUser(null)} />}
      </Modal>
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New User">
        <CreateUserForm onSuccess={() => { setShowCreate(false); load(); }} onClose={() => setShowCreate(false)} />
      </Modal>
    </div>
  );
}

// ── API KEYS TAB ──────────────────────────────────────────────────────────────
export function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = () => settingsApi.listApiKeys().then(({ data }) => setKeys(data)).catch(() => toast.error('Failed'));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!label.trim()) return;
    setCreating(true);
    try { const { data } = await settingsApi.createApiKey(label.trim()); setNewKey(data); setLabel(''); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setCreating(false); }
  };

  const revoke = async id => { try { await settingsApi.revokeApiKey(id); load(); toast.success('Revoked'); } catch { toast.error('Failed'); } };
  const del    = async id => { if (!confirm('Delete key?')) return; try { await settingsApi.deleteApiKey(id); load(); toast.success('Deleted'); } catch { toast.error('Failed'); } };

  return (
    <div className="space-y-5">
      {newKey && (
        <div className="rounded-xl p-4" style={{ background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.25)' }}>
          <div className="flex items-start justify-between mb-2">
            <p className="text-sm font-semibold" style={{ color: 'var(--warning)' }}>⚠️ Save this key now — it won't be shown again</p>
            <button onClick={() => setNewKey(null)} className="text-xl ml-3" style={{ color: 'var(--textDim)' }}>×</button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <code className="flex-1 rounded-lg px-3 py-2 text-xs font-mono break-all" style={{ background: 'var(--bg)', color: 'var(--warning)', border: '1px solid var(--border)' }}>
              {newKey.plaintext}
            </code>
            <button onClick={() => copyText(newKey.plaintext)} className="btn-primary text-xs px-3 flex-shrink-0">Copy</button>
          </div>
        </div>
      )}

      <div className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text)' }}>Create API Access Key</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={label} onChange={e => setLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()}
            placeholder="Name this key, e.g. Zapier Production" className="input flex-1 text-sm" />
          <button onClick={create} disabled={!label.trim() || creating} className="btn-primary text-sm px-4 flex-shrink-0">
            {creating ? '…' : 'Create Key'}
          </button>
        </div>
      </div>

      {keys.length === 0
        ? <div className="rounded-xl p-10 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <p className="text-3xl mb-2">🔑</p>
            <p className="text-sm" style={{ color: 'var(--textMuted)' }}>No API access keys yet.</p>
          </div>
        : <div className="rounded-xl overflow-hidden responsive-scroll" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Label','Key','Status','Last Used','Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--textDim)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id} style={{ borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td className="px-4 py-3 font-semibold" style={{ color: 'var(--text)' }}>{k.label}</td>
                    <td className="px-4 py-3"><code className="text-xs font-mono" style={{ color: 'var(--textMuted)' }}>{k.keyPrefix}••••••••</code></td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                        style={{ background: k.isActive ? 'rgba(34,201,122,.1)' : 'var(--surface)', color: k.isActive ? 'var(--success)' : 'var(--textDim)', border: '1px solid var(--border)' }}>
                        {k.isActive ? 'Active' : 'Revoked'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--textDim)' }}>{k.lastUsed ? new Date(k.lastUsed).toLocaleString() : 'Never'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3 text-xs">
                        {k.isActive && <button onClick={() => revoke(k.id)} style={{ color: 'var(--warning)' }} className="hover:opacity-70">Revoke</button>}
                        <button onClick={() => del(k.id)} style={{ color: 'var(--danger)' }} className="hover:opacity-70">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
    </div>
  );
}

// ── SHORT LINKS TAB ───────────────────────────────────────────────────────────
export function ShortLinksTab() {
  const [links, setLinks] = useState([]);
  const [label, setLabel] = useState('');
  const [originalUrl, setOriginalUrl] = useState('');
  const [creating, setCreating] = useState(false);

  const load = () => linksApi.list()
    .then(({ data }) => setLinks(data || []))
    .catch(() => toast.error('Could not load short links'));

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!originalUrl.trim()) return toast.error('Paste a destination URL');
    setCreating(true);
    try {
      const { data } = await linksApi.create({ label: label.trim(), originalUrl: originalUrl.trim() });
      setLinks((current) => [data, ...current]);
      setLabel('');
      setOriginalUrl('');
      copyText(data.shortUrl);
      toast.success('Short link created and copied');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create short link');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this short link? Existing messages using it will stop redirecting.')) return;
    try {
      await linksApi.delete(id);
      setLinks((current) => current.filter((link) => link.id !== id));
      toast.success('Short link deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete short link');
    }
  };

  return (
    <div className="space-y-5">
      <SectionCard title="Create Short Link" description="Shorten WhatsApp community links, ticket links, promo pages, or any URL you want to place inside SMS campaigns and automations.">
        <div className="grid sm:grid-cols-[1fr_1.5fr_auto] gap-3 sm:items-end">
          <Field label="Label">
            <input className="input" value={label} onChange={e => setLabel(e.target.value)} placeholder="WhatsApp community" />
          </Field>
          <Field label="Destination URL">
            <input className="input" value={originalUrl} onChange={e => setOriginalUrl(e.target.value)} placeholder="https://chat.whatsapp.com/..." />
          </Field>
          <button type="button" onClick={create} disabled={creating} className="btn-primary text-sm px-4 h-10">
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </SectionCard>

      <SectionCard title="Saved Short Links" description="Copy these into SMS templates, keyword replies, campaign messages, or automation nodes. Clicks are tracked per workspace.">
        {links.length === 0 ? (
          <div className="rounded-xl p-8 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>No short links yet</p>
            <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>Create one for tickets, WhatsApp groups, coupons, or event pages.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {links.map((link) => (
              <div key={link.id} className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex flex-col lg:flex-row lg:items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{link.label || 'Short link'}</p>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--accentSoft)', color: 'var(--accent)' }}>{link.clickCount} clicks</span>
                    </div>
                    <p className="text-xs break-anywhere mt-1" style={{ color: 'var(--textMuted)' }}>{link.originalUrl}</p>
                    <code className="block text-xs mt-2 rounded-lg px-2 py-1 break-anywhere" style={{ background: 'var(--bg)', color: 'var(--accent)', border: '1px solid var(--border)' }}>{link.shortUrl}</code>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button type="button" onClick={() => copyText(link.shortUrl)} className="btn-secondary text-xs px-3">Copy</button>
                    <button type="button" onClick={() => remove(link.id)} className="btn-secondary text-xs px-3" style={{ color: 'var(--danger)' }}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ── AI AGENTS TAB ─────────────────────────────────────────────────────────────
const AGENT_SCOPES = [
  ['workspace:read', 'Workspace summary'],
  ['contacts:read', 'Search contacts'],
  ['contacts:write', 'Create/update contacts'],
  ['messages:read', 'Read messages'],
  ['messages:send', 'Send SMS/WhatsApp'],
  ['campaigns:read', 'Read campaigns'],
  ['campaigns:write', 'Create campaigns'],
  ['automations:read', 'Read automations'],
  ['automations:write', 'Create automations'],
  ['settings:read', 'Read setup status'],
];

function WorkspaceAiConfigCard() {
  const [config, setConfig] = useState({ provider: 'openai', model: '', openaiApiKey: '', anthropicApiKey: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    aiAgentApi.getConfig()
      .then(({ data }) => setConfig((current) => ({ ...current, ...data, openaiApiKey: '', anthropicApiKey: '' })))
      .catch(() => toast.error('Could not load workspace AI keys'))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, value) => setConfig((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = aiConfigSchema.parse(config);
      const { data } = await aiAgentApi.updateConfig(payload);
      setConfig((current) => ({ ...current, ...data, openaiApiKey: '', anthropicApiKey: '', clearOpenAIKey: false, clearClaudeKey: false }));
      toast.success('Workspace AI keys saved');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not save AI keys');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Workspace AI Keys" description="Ask AI and Automation AI nodes use this customer's own OpenAI or Claude key. Keys are encrypted and never shared with another workspace.">
      {loading ? <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Loading AI settings...</p> : (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Default provider">
              <select className="input" value={config.provider || 'openai'} onChange={e => update('provider', e.target.value)}>
                <option value="openai">OpenAI</option>
                <option value="claude">Claude</option>
              </select>
            </Field>
            <Field label="Default model">
              <input className="input" value={config.model || ''} onChange={e => update('model', e.target.value)} placeholder={config.provider === 'claude' ? 'claude-sonnet-4-20250514' : 'gpt-5.1'} />
            </Field>
            <Field label={config.hasOpenAIKey ? 'OpenAI API key saved' : 'OpenAI API key'}>
              <input className="input font-mono text-xs" type="password" value={config.openaiApiKey || ''} onChange={e => update('openaiApiKey', e.target.value)} placeholder={config.hasOpenAIKey ? 'Saved. Paste new key to replace.' : 'sk-...'} />
            </Field>
            <Field label={config.hasClaudeKey ? 'Claude API key saved' : 'Claude API key'}>
              <input className="input font-mono text-xs" type="password" value={config.anthropicApiKey || ''} onChange={e => update('anthropicApiKey', e.target.value)} placeholder={config.hasClaudeKey ? 'Saved. Paste new key to replace.' : 'sk-ant-...'} />
            </Field>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-col sm:flex-row gap-3 text-xs" style={{ color: 'var(--textMuted)' }}>
              <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(config.clearOpenAIKey)} onChange={e => update('clearOpenAIKey', e.target.checked)} /> Clear OpenAI key</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(config.clearClaudeKey)} onChange={e => update('clearClaudeKey', e.target.checked)} /> Clear Claude key</label>
            </div>
            <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm px-4">
              {saving ? 'Saving...' : 'Save AI Keys'}
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

export function AiAgentsTab() {
  const tenant = useAuthStore((s) => s.tenant);
  const setTenant = useAuthStore((s) => s.setTenant);
  const [connections, setConnections] = useState([]);
  const [mcpUrl, setMcpUrl] = useState('');
  const [name, setName] = useState('ChatGPT assistant');
  const [selectedScopes, setSelectedScopes] = useState(['workspace:read', 'contacts:read', 'campaigns:read']);
  const [creating, setCreating] = useState(false);
  const [savingBubble, setSavingBubble] = useState(false);
  const [newConnection, setNewConnection] = useState(null);

  const load = () => settingsApi.listAgentConnections()
    .then(({ data }) => {
      setConnections(data.connections || []);
      setMcpUrl(data.mcpUrl || '');
    })
    .catch(() => toast.error('Failed to load agent connections'));

  useEffect(() => { load(); }, []);

  const toggleScope = (scope) => {
    setSelectedScopes((current) => current.includes(scope)
      ? current.filter((s) => s !== scope)
      : [...current, scope]);
  };

  const create = async () => {
    if (!name.trim()) return toast.error('Enter a connection name');
    if (!selectedScopes.length) return toast.error('Select at least one scope');
    setCreating(true);
    try {
      const { data } = await settingsApi.createAgentConnection({
        name: name.trim(),
        clientName: name.trim(),
        scopes: selectedScopes,
      });
      setNewConnection(data);
      toast.success('Agent connection created');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create agent connection');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!confirm('Revoke this agent connection?')) return;
    try {
      await settingsApi.revokeAgentConnection(id);
      toast.success('Agent connection revoked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not revoke connection');
    }
  };

  const setAskAiVisible = async (enabled) => {
    setSavingBubble(true);
    try {
      const { data } = await settingsApi.updateWorkspace({ askAiBubbleEnabled: enabled });
      setTenant(data);
      toast.success(enabled ? 'Ask AI bubble enabled' : 'Ask AI bubble hidden');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update Ask AI visibility');
    } finally {
      setSavingBubble(false);
    }
  };

  return (
    <div className="space-y-5">
      <WorkspaceAiConfigCard />

      <SectionCard title="In-App Ask AI" description="Control the floating assistant bubble for this customer's workspace. Outside MCP connections keep working even when the bubble is hidden.">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Show Ask AI bubble</p>
            <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>
              Turn this off if the customer wants a simpler interface or does not want the assistant visible.
            </p>
          </div>
          <button
            type="button"
            disabled={savingBubble}
            onClick={() => setAskAiVisible(tenant?.askAiBubbleEnabled === false)}
            className="px-4 py-2 rounded-lg text-sm font-bold transition-colors"
            style={{
              background: tenant?.askAiBubbleEnabled === false ? 'var(--surface)' : 'var(--accent)',
              color: tenant?.askAiBubbleEnabled === false ? 'var(--textMuted)' : '#fff',
              border: '1px solid var(--border)',
            }}
          >
            {savingBubble ? 'Saving...' : tenant?.askAiBubbleEnabled === false ? 'Hidden' : 'Visible'}
          </button>
        </div>
      </SectionCard>

      {newConnection && (
        <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.25)' }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--warning)' }}>Save these agent credentials now</p>
              <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>The client secret and live token cannot be shown again.</p>
            </div>
            <button onClick={() => setNewConnection(null)} className="text-xl" style={{ color: 'var(--textDim)' }}>×</button>
          </div>
          {[
            ['MCP URL', newConnection.mcpUrl],
            ['Client ID', newConnection.clientId],
            ['Client Secret', newConnection.clientSecret],
            ['Live Bearer Token', newConnection.accessToken],
          ].map(([label, value]) => (
            <CopyRow key={label} label={label} value={value} />
          ))}
        </div>
      )}

      <div className="rounded-xl p-4 space-y-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Connect an Outside AI Assistant</p>
          <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>Use this for ChatGPT, Claude, n8n, or custom AI tools. Give only the permissions the assistant needs.</p>
        </div>
        <Field label="Connection name">
          <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="ChatGPT assistant" />
        </Field>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--textDim)' }}>Allowed actions</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {AGENT_SCOPES.map(([scope, label]) => (
              <label key={scope} className="flex items-center gap-2 rounded-lg px-3 py-2 cursor-pointer"
                style={{ background: selectedScopes.includes(scope) ? 'var(--accentSoft)' : 'var(--surface)', border: '1px solid var(--border)' }}>
                <input type="checkbox" checked={selectedScopes.includes(scope)} onChange={() => toggleScope(scope)} style={{ accentColor: 'var(--accent)' }} />
                <span className="text-xs font-semibold" style={{ color: selectedScopes.includes(scope) ? 'var(--accentHot)' : 'var(--textMuted)' }}>{label}</span>
              </label>
            ))}
          </div>
        </div>
        <button onClick={create} disabled={creating} className="btn-primary text-sm">
          {creating ? 'Creating...' : 'Create AI Connection'}
        </button>
      </div>

      <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-semibold mb-1" style={{ color: 'var(--textDim)' }}>AI connection URL</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <code className="flex-1 min-w-0 rounded-lg px-3 py-2 text-xs font-mono break-anywhere" style={{ background: 'var(--bg)', color: 'var(--accent)', border: '1px solid var(--border)' }}>
            {mcpUrl || 'https://app.ineedleadcrm.com/api/mcp'}
          </code>
          <button onClick={() => copyText(mcpUrl || 'https://app.ineedleadcrm.com/api/mcp')} className="btn-secondary text-xs">Copy</button>
        </div>
      </div>

      {connections.length === 0
        ? <div className="rounded-xl p-10 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <p className="text-sm" style={{ color: 'var(--textMuted)' }}>No outside AI assistants connected yet.</p>
          </div>
        : <div className="rounded-xl overflow-hidden responsive-scroll" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Name','Client','Scopes','Status','Last Used','Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--textDim)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {connections.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-4 py-3 font-semibold" style={{ color: 'var(--text)' }}>{c.name}</td>
                    <td className="px-4 py-3"><code className="text-xs" style={{ color: 'var(--textMuted)' }}>{c.clientId}</code></td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--textMuted)' }}>{c.scopes.join(', ')}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: c.isActive ? 'var(--success)' : 'var(--textDim)' }}>{c.isActive ? 'Active' : 'Revoked'}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--textDim)' }}>{c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : 'Never'}</td>
                    <td className="px-4 py-3">
                      {c.isActive && <button onClick={() => revoke(c.id)} className="text-xs hover:opacity-70" style={{ color: 'var(--danger)' }}>Revoke</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
    </div>
  );
}

// ── APPEARANCE TAB ───────────────────────────────────────────────────────────
export function AppearanceTab() {
  const { config, presets, setMode, updateTheme, resetTheme } = useTheme();
  const activePreset = presets[config.preset] || presets.indigo;

  const OptionButton = ({ active, children, onClick }) => (
    <button
      onClick={onClick}
      className="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
      style={{
        background: active ? 'var(--accent)' : 'var(--card)',
        color: active ? '#fff' : 'var(--textMuted)',
        border: `1px solid ${active ? 'var(--accentHot)' : 'var(--border)'}`,
      }}
    >
      {children}
    </button>
  );

  return (
    <div className="space-y-5">
      <div className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Theme Mode</p>
            <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>Choose the base interface mode.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <OptionButton active={config.mode === 'dark'} onClick={() => setMode('dark')}>Dark</OptionButton>
            <OptionButton active={config.mode === 'light'} onClick={() => setMode('light')}>Light</OptionButton>
          </div>
        </div>
      </div>

      <div className="rounded-xl p-4 space-y-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Accent Theme</p>
          <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>Keep the black/red identity, but tune the red shade.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(presets).map(([key, preset]) => {
            const active = config.preset === key && !config.customAccent;
            return (
              <button
                key={key}
                onClick={() => updateTheme({ preset: key, customAccent: '' })}
                className="rounded-xl p-3 text-left transition-all"
                style={{
                  background: active ? 'var(--accentSoft)' : 'var(--surface)',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                }}
              >
                <span className="block w-full h-7 rounded-lg mb-2" style={{ background: preset.accent, border: `1px solid ${preset.accentHot}` }} />
                <span className="text-xs font-semibold" style={{ color: active ? 'var(--accentHot)' : 'var(--text)' }}>{preset.label}</span>
              </button>
            );
          })}
        </div>
        <Field label="Custom brand colour">
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={config.customAccent || activePreset.accent}
              onChange={e => updateTheme({ customAccent: e.target.value })}
              className="w-12 h-10 rounded-lg flex-shrink-0"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
            />
            <input
              value={config.customAccent || ''}
              onChange={e => updateTheme({ customAccent: e.target.value })}
              placeholder={activePreset.accent}
              className="input font-mono text-xs"
            />
          </div>
        </Field>
      </div>

      <div className="rounded-xl p-4 space-y-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Interface Feel</p>
          <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>Adjust spacing and corner style for recording or daily use.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--textDim)' }}>Density</p>
            <div className="flex gap-2 flex-wrap">
              <OptionButton active={config.density === 'compact'} onClick={() => updateTheme({ density: 'compact' })}>Compact</OptionButton>
              <OptionButton active={config.density === 'comfortable'} onClick={() => updateTheme({ density: 'comfortable' })}>Comfortable</OptionButton>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--textDim)' }}>Corners</p>
            <div className="flex gap-2 flex-wrap">
              {['sharp','modern','soft'].map(radius => (
                <OptionButton key={radius} active={config.radius === radius} onClick={() => updateTheme({ radius })}>
                  {radius[0].toUpperCase() + radius.slice(1)}
                </OptionButton>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text)' }}>Live Preview</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="rounded-xl p-3" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--textMuted)' }}>Campaign</p>
            <button className="btn-primary w-full text-xs">Send Bulk SMS</button>
          </div>
          <div className="rounded-xl p-3" style={{ background: 'var(--card)', border: '1px solid var(--borderHi)' }}>
            <p className="text-xs font-semibold" style={{ color: 'var(--accentHot)' }}>Automation Active</p>
            <p className="text-[10px] mt-1" style={{ color: 'var(--textDim)' }}>HTTP, AI, SMS, waits, branches</p>
          </div>
          <div className="rounded-xl p-3" style={{ background: 'var(--accentSoft)', border: '1px solid var(--borderHi)' }}>
            <p className="text-xs font-semibold" style={{ color: 'var(--accentHot)' }}>Opt-out Protected</p>
            <p className="text-[10px] mt-1" style={{ color: 'var(--textMuted)' }}>STOP handling enabled</p>
          </div>
        </div>
      </div>

      <button onClick={resetTheme} className="btn-secondary text-sm">Reset Appearance</button>
    </div>
  );
}

export function MobileAppTab() {
  return (
    <div className="space-y-5">
      <SectionCard
        title="Download Mobile App"
        description="Install the Android app for mobile inbox, contacts, campaigns, and voice calling when calls are enabled for this workspace."
      >
        <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-center">
          <div className="rounded-xl border p-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ background: 'var(--accent)' }}>
                <IconPhone size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>Bulk SMS CRM for Android</p>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--textMuted)' }}>
                  Use the same email and password from this workspace. Android may ask permission to install from the browser and to use the microphone for calls.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
            <a className="btn-primary justify-center text-sm" href="/downloads/app-arm64-v8a-release.apk" download>
              Download Android App
            </a>
            <a className="btn-secondary justify-center text-sm" href="/mobile-app">
              Open Download Page
            </a>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="If Installation Fails" description="Most modern phones use the main Android download. Older phones may need the fallback file.">
        <div className="flex flex-col sm:flex-row gap-2">
          <a className="btn-secondary justify-center text-sm" href="/downloads/app-armeabi-v7a-release.apk" download>
            Download Older Android Fallback
          </a>
          <button type="button" className="btn-secondary justify-center text-sm" onClick={() => copyText(`${window.location.origin}/mobile-app`)}>
            Copy Download Link
          </button>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--textMuted)' }}>
          iPhone installation requires TestFlight or App Store distribution. The website download is for Android.
        </p>
      </SectionCard>

      <SectionCard title="Call Setup Reminder" description="The mobile app can log in without voice setup, but calling only works when voice access is enabled.">
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {[
            'Calls enabled for this workspace',
            'Twilio Voice API Key SID and Secret saved',
            'TwiML App SID saved',
            'Mobile app call notifications enabled',
          ].map((item) => (
            <div key={item} className="rounded-xl border px-3 py-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--textMuted)' }}>
              {item}
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

export function PaymentTab() {
  const tenant = useAuthStore(s => s.tenant);
  const setTenant = useAuthStore(s => s.setTenant);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paid, setPaid] = useState(false);

  const load = () => {
    setLoading(true);
    setError('');
    paymentsApi.getConfig()
      .then(({ data }) => setConfig(data))
      .catch((err) => setError(err.response?.data?.message || 'Could not load payment setup'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCheckout = async () => {
    try {
      setError('');
      const paddleConfig = config?.paddle;
      const items = paddleCheckoutItems(paddleConfig?.items);
      if (!paddleConfig?.enabled || !items.length) throw new Error(paddleConfig?.error || 'Paddle checkout is not configured yet');
      const Paddle = await loadPaddleSdk({
        clientToken: paddleConfig.clientToken,
        environment: paddleConfig.environment,
        eventCallback: async (event) => {
          if (event?.name !== 'checkout.completed') return;
          try {
            const transactionId = event?.data?.transaction_id || event?.data?.transactionId;
            if (!transactionId) return toast.error('Paddle did not return a transaction ID');
            const response = await paymentsApi.completePaddleCheckout(transactionId, {
              features: workspace?.features,
              billingPeriod: workspace?.billingPeriod,
            });
            if (response.data?.tenant) setTenant({ ...(tenant || {}), ...response.data.tenant });
            setPaid(true);
            toast.success('Payment received. Workspace is active.');
            load();
          } catch (err) {
            const message = err.response?.data?.message || err.message || 'Payment was received, but activation could not be confirmed yet';
            setError(message);
            toast.error(message);
          }
        },
      });
      Paddle.Checkout.open({
        items,
        customer: { email: tenant?.ownerEmail || undefined },
        customData: { tenantId: workspace?.id, workspace: workspace?.name, features: workspace?.features, billingPeriod: workspace?.billingPeriod },
        settings: {
          displayMode: 'overlay',
          variant: 'one-page',
          successUrl: `${window.location.origin}/welcome`,
        },
      });
    } catch (err) {
      setError(err.message || 'Could not open Paddle checkout');
    }
  };

  const workspace = config?.workspace;
  const amount = workspace?.amountCents || 0;
  const dueLabel = workspace?.accessType === 'LIFETIME' ? 'one-time lifetime access' : 'next 30 days';
  const trialEndsAt = workspace?.currentPeriodEndsAt ? new Date(workspace.currentPeriodEndsAt) : null;
  const trialLabel = trialEndsAt && !Number.isNaN(trialEndsAt.getTime())
    ? trialEndsAt.toLocaleString()
    : 'Not set';
  const openPortal = async () => {
    try {
      const { data } = await paymentsApi.createPortalSession();
      if (!data?.url) throw new Error('Billing portal is not available yet');
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not open billing portal');
    }
  };

  if (loading) {
    return <SectionCard title="Paddle Payment" description="Loading payment setup..."><p className="text-sm" style={{ color: 'var(--textMuted)' }}>Checking your workspace plan.</p></SectionCard>;
  }

  if (paid || workspace?.billingStatus === 'PAID') {
    return (
      <SectionCard title="Payment Active" description="This workspace is currently active.">
        <div className="rounded-xl border p-4" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
          <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{workspace?.planName || tenant?.planName || 'Active workspace'}</p>
          <p className="mt-1 text-sm" style={{ color: 'var(--textMuted)' }}>
            Status: paid{workspace?.accessType === 'MONTHLY' && workspace?.currentPeriodEndsAt ? ` until ${trialLabel}` : ''}.
          </p>
          {workspace?.accessType === 'MONTHLY' && (
            <p className="mt-2 text-sm" style={{ color: 'var(--textMuted)' }}>
              To remove paid modules for the next billing cycle, use billing management below. Access changes are applied only after Paddle confirms the subscription update, so workspace features cannot be bypassed from the browser.
            </p>
          )}
          {workspace?.accessType === 'LIFETIME' && (
            <p className="mt-2 text-sm" style={{ color: 'var(--textMuted)' }}>
              Lifetime access is managed internally by the platform owner and is not changed through Paddle.
            </p>
          )}
        </div>
        {workspace?.paddleCustomerId && (
          <button type="button" onClick={openPortal} className="btn-primary mt-4">
            Manage billing and modules
          </button>
        )}
      </SectionCard>
    );
  }

  return (
    <div className="space-y-4">
      <SectionCard title="Paddle Payment" description="Use secure Paddle checkout to activate this workspace after the free trial.">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border p-4 sm:col-span-2" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--textDim)' }}>Selected plan</p>
            <p className="mt-1 text-xl font-bold" style={{ color: 'var(--text)' }}>{workspace?.planName || tenant?.planName || 'Workspace access'}</p>
            <p className="mt-2 text-sm" style={{ color: 'var(--textMuted)' }}>
              Trial ends: {trialLabel}
            </p>
            <p className="mt-1 text-sm" style={{ color: 'var(--textMuted)' }}>
              Payment covers {dueLabel}. Your workspace becomes active immediately after Paddle confirms payment.
            </p>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: 'var(--border)', background: 'var(--accentSoft)' }}>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--textDim)' }}>Due after trial</p>
            <p className="mt-1 text-4xl font-bold" style={{ color: 'var(--accent)' }}>{money(amount, config?.paddle?.currency)}</p>
            <p className="mt-1 text-xs font-bold" style={{ color: 'var(--textMuted)' }}>{workspace?.accessType === 'LIFETIME' ? 'Lifetime' : 'Monthly'}</p>
          </div>
        </div>

        {!config?.paddle?.enabled ? (
          <div className="mt-4 rounded-xl border p-4" style={{ borderColor: 'var(--warning)', background: 'rgba(245,166,35,.08)' }}>
            <p className="font-bold" style={{ color: 'var(--text)' }}>Paddle is not configured yet</p>
            <p className="mt-1 text-sm" style={{ color: 'var(--textMuted)' }}>
              Add PADDLE_API_KEY, PADDLE_CLIENT_TOKEN, webhook secret, and tier price IDs on the server, then restart the API.
            </p>
          </div>
        ) : (
          <div className="mt-4">
            <button type="button" onClick={openCheckout} className="btn-primary w-full justify-center">
              Open secure checkout
            </button>
          </div>
        )}
        {error && <p className="mt-3 text-sm font-bold" style={{ color: 'var(--danger)' }}>{error}</p>}
      </SectionCard>
    </div>
  );
}

// ── MAIN SETTINGS PAGE ────────────────────────────────────────────────────────
// Channel setup (Twilio, A2P, WhatsApp, Email, webhooks) lives on the Channels page,
// and Short links has its own page; old ?section= keys redirect there.
const LEGACY_SECTIONS = {
  connections: '/channels',
  numbers: '/channels/sms',
  a2p: '/channels/sms?tab=compliance',
  webhooks: '/channels/sms?tab=webhooks',
  directwa: '/channels/whatsapp?tab=api',
  email: '/channels/email',
  shortlinks: '/links',
};
const SECTION_ALIASES = {
  whitelabel: 'profile', users: 'team', payment: 'billing', apikeys: 'developers', agents: 'developers', endpoints: 'developers', mobileapp: 'mobile',
};

function DevelopersSection() {
  const tenant = useAuthStore(s => s.tenant);
  const parts = [
    hasApiAccess(tenant) && ['API keys', 'Keys for Zapier, n8n and your own integrations.', <ApiKeysTab key="api" />],
    hasAutomation(tenant) && ['AI assistants', 'Let ChatGPT, Claude or n8n work in this workspace with limited permissions.', <AiAgentsTab key="ai" />],
    (hasSms(tenant) || hasCalls(tenant) || hasAutomation(tenant)) && ['Webhook domains', 'Which live domain the platform uses when it builds webhook links.', <EndpointsTab key="ep" />],
  ].filter(Boolean);
  return (
    <div className="space-y-10">
      {parts.map(([title, text, node]) => (
        <section key={title}>
          <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>{title}</h2>
          <p className="text-sm mb-4" style={{ color: 'var(--textMuted)' }}>{text}</p>
          {node}
        </section>
      ))}
    </div>
  );
}

const SECTIONS = [
  { key: 'profile', group: 'Workspace', Icon: IconGlobe, label: 'Workspace profile', title: 'Workspace profile', subtitle: 'Brand name, support email, logo, domains and account options.',
    render: () => <div className="space-y-6"><WeeklySummaryCard /><WhiteLabelTab /></div> },
  { key: 'team', group: 'Workspace', Icon: IconContacts, label: 'Team', title: 'Team', subtitle: 'Add teammates, set their role and reset passwords. Agents only see conversations assigned to them.',
    render: () => <UsersTab /> },
  { key: 'replies', group: 'Workspace', Icon: IconNote, label: 'Saved replies', title: 'Saved replies', subtitle: 'Answers your team sends often, ready to insert in one click.',
    allowed: (tenant) => hasInbox(tenant), render: () => <SavedRepliesManager /> },
  { key: 'billing', group: 'Account', Icon: IconDollar, label: 'Billing and plan', title: 'Billing and plan', subtitle: 'Your plan, trial, payment method and invoices.',
    render: () => <PaymentTab /> },
  { key: 'developers', group: 'Account', Icon: IconKey, label: 'Developers', title: 'Developers', subtitle: 'API keys, AI assistants and webhook domains.',
    allowed: (tenant) => hasApiAccess(tenant) || hasAutomation(tenant) || hasSms(tenant) || hasCalls(tenant), render: () => <DevelopersSection /> },
  { key: 'activity', group: 'Account', Icon: IconClock, label: 'Activity log', title: 'Activity log', subtitle: 'Who signed in, sent campaigns, exported data or changed settings.',
    render: () => <ActivityLogSection /> },
  { key: 'appearance', group: 'Personal', Icon: IconSun, label: 'Appearance', title: 'Appearance', subtitle: 'Colour, density and corner style for this device.',
    render: () => <AppearanceTab /> },
  { key: 'mobile', group: 'Personal', Icon: IconPhone, label: 'Mobile app', title: 'Mobile app', subtitle: 'Download the Android app and share the install link with your team.',
    allowed: (tenant) => hasInbox(tenant) || hasCalls(tenant), render: () => <MobileAppTab /> },
];

export default function Settings() {
  const tenant = useAuthStore(s => s.tenant);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const available = SECTIONS.filter((item) => !item.allowed || item.allowed(tenant));
  const requested = searchParams.get('section') || '';
  const key = SECTION_ALIASES[requested] || requested;
  const active = available.find((item) => item.key === key) || available[0];
  const setSection = (next) => setSearchParams(next === available[0].key ? {} : { section: next }, { replace: true });
  const swipeProps = useSwipeNavigation(available.map((item) => item.key), active.key, setSection);
  const groups = [...new Set(available.map((item) => item.group))];

  useEffect(() => {
    if (LEGACY_SECTIONS[requested]) navigate(LEGACY_SECTIONS[requested], { replace: true });
  }, [requested, navigate]);

  return (
    <div className="flex flex-col md:flex-row h-full overflow-hidden max-w-full" {...swipeProps}>
      {/* Phone: horizontal section tabs */}
      <div className="md:hidden flex-shrink-0" style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <div className="flex overflow-x-auto no-scrollbar px-2 py-2 gap-1">
          {available.map((item) => (
            <button key={item.key} type="button" onClick={() => setSection(item.key)}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              style={{ background: active.key === item.key ? 'var(--accentSoft)' : 'transparent', color: active.key === item.key ? 'var(--accentHot)' : 'var(--textMuted)' }}>
              <item.Icon size={13} />{item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop: section list */}
      <div className="hidden md:flex flex-col w-60 flex-shrink-0 py-6 px-3 min-h-0" style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)' }}>
        <p className="text-[15px] font-semibold px-3 mb-4" style={{ color: 'var(--text)' }}>Settings</p>
        <nav className="space-y-5 overflow-y-auto pb-8 min-h-0 no-scrollbar">
          {groups.map((group) => (
            <div key={group}>
              <p className="side-nav-label">{group}</p>
              <div className="space-y-1">
                {available.filter((item) => item.group === group).map((item) => (
                  <button key={item.key} type="button" onClick={() => setSection(item.key)} className={`side-nav-link w-full ${active.key === item.key ? 'active' : ''}`}>
                    <item.Icon size={17} className="flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="pt-2" style={{ borderTop: '1px solid var(--border)' }}>
            <p className="side-nav-label mt-3">Moved</p>
            <Link to="/channels" className="side-nav-link"><IconLink size={17} /><span>Channel setup</span></Link>
          </div>
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden px-4 py-6 sm:px-8 sm:py-8 mobile-safe-page md:pb-8">
        <div className="max-w-5xl mx-auto min-w-0">
          <div className="mb-7">
            <p className="eyebrow">{active.group}</p>
            <h1 className="page-title">{active.title}</h1>
            <p className="page-subtitle">{active.subtitle}</p>
          </div>
          <div key={active.key}>{active.render()}</div>
        </div>
      </div>
    </div>
  );
}
